import API_BASE from "./apiConfig";
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaUser, FaEnvelope, FaPhone, FaCalendar, FaTransgender,
  FaImage, FaUserFriends, FaUserPlus, FaSave, FaTimesCircle,
  FaSearch, FaRegClone, FaDownload, FaUpload,
  FaGraduationCap, FaCheckCircle, FaExclamationCircle, FaChild,
  FaBaseballBall, FaIdCard, FaBirthdayCake, FaShieldAlt,
  FaTrophy, FaTshirt, FaUserEdit, FaChevronDown, FaChevronUp,
  FaFileAlt, FaPencilAlt, FaTrash, FaPlus, FaCreditCard,
  FaCalendarAlt, FaStickyNote, FaFileSignature, FaClipboardList,
  FaDollarSign, FaHistory, FaCheckSquare, FaRegClock, FaTimes, FaArrowRight, FaEdit,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { PrimaryButton, GhostButton, DangerButton } from "./components/ThemedButton";
import ParentAccessPanel from "./ParentAccessPanel";
import TournamentStudentTab from "./pages/TournamentStudentTab";
import { useTheme, getT, getBrandColors, darken } from "./ThemeContext";
import { useAgeCategories, categoryForAge } from "./hooks/useLookups";
import { useAppConfig } from "./AppContext";
import { useSort } from "./hooks/useSort";
import { fmtDate, fmtTime } from "./utils/formatters";

const BASE = API_BASE;

// Getters (not plain values) so every ST.xxx read reflects the current
// app_config brand colors live — a plain object captured at module-import
// time would go stale the instant an admin changes a color in App Settings.
const ST = {
  blur:        "blur(20px)",
  cardRadius:  "16px",
  inputRadius: "10px",
  get yellow()   { return getBrandColors().yellow; },
  get blue()     { return getBrandColors().blue; },
  get blueDark() { return getBrandColors().blueDark; },
  get yellowGrad() { const y = getBrandColors().yellow; return `linear-gradient(135deg,${y},${darken(y)})`; },
  get green()  { return getBrandColors().green; },
  get red()    { return getBrandColors().red; },
  get orange() { return getBrandColors().orange; },
  font:        "'Segoe UI', system-ui, sans-serif",
};

// Codes/brackets come from the live age_categories table (useAgeCategories() +
// categoryForAge(), see hooks/useLookups.js) — this file used to have its own
// "U10"-based brackets that didn't match the real U9/U11/U13/U15/U17/Adult codes.
const AGE_CAT_COLORS = {
  U9:    { bg: "rgba(251,191,36,0.12)",  border: ST.orange, text: ST.orange },
  U11:   { bg: "rgba(52,211,153,0.12)",  border: ST.green, text: ST.green },
  U13:   { bg: "rgba(96,165,250,0.12)",  border: "#60a5fa", text: "#60a5fa" },
  U15:   { bg: "rgba(167,139,250,0.12)", border: "#a78bfa", text: "#a78bfa" },
  U17:   { bg: "rgba(244,114,182,0.12)", border: "#f472b6", text: "#f472b6" },
  Adult: { bg: "rgba(148,163,184,0.12)", border: "#94a3b8", text: "#94a3b8" },
};

// ─── Style helpers ────────────────────────────────────────────────────────────
const labelSt    = (T) => ({ display: "block", marginBottom: "7px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLabel, fontFamily: ST.font });
const inputWrapSt= (T) => ({ display: "flex", alignItems: "center", border: `1.5px solid ${T.inputBdr}`, borderRadius: ST.inputRadius, padding: "10px 14px", background: T.inputBg, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, width: "100%", boxSizing: "border-box" });
const inputSt    = (T) => ({ border: "none", outline: "none", width: "100%", background: "transparent", fontSize: "14px", color: T.inputText, fontFamily: ST.font });
const lightText  = (T) => ({ color: T.textMuted, fontWeight: 500, marginLeft: "4px", textTransform: "none", letterSpacing: 0 });
const glassCard  = (T) => ({ background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, border: T.glassBdr, borderRadius: ST.cardRadius, boxShadow: T.glassSh, padding: "32px 36px", boxSizing: "border-box" });
const yellowBtn  = (T) => ({ padding: "10px 20px", borderRadius: "10px", fontSize: "13px", cursor: "pointer", WebkitTapHighlightColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", ...T.btnPrimary });
const toastSt    = (ok) => ({ display: "flex", alignItems: "center", gap: "10px", padding: "13px 18px", borderRadius: "10px", marginBottom: "20px", fontSize: "13px", fontWeight: 600, background: ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)", color: ok ? ST.green : ST.red, border: ok ? "1px solid rgba(52,211,153,0.30)" : "1px solid rgba(248,113,113,0.30)", backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur });

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

function computeAgeCategory(dobStr, categories) {
  if (!dobStr) return null;
  const dob   = new Date(dobStr + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) age--;
  return categoryForAge(age, categories);
}

const blankForm = (today) => ({
  first_name: "", last_name: "", date_of_birth: "", gender: "",
  parent_name: "", parent_email: "", parent_phone: "",
  street_address: "", city: "", state: "", zip_code: "",
  join_date: today, active_status: 1, skill_level_id: "",
  playing_role_id: "", cricclub_player_id: "",
  tournament_ready: false, jersey_purchased_date: "",
  photo_file: null, existing_photo_url: "",
});

// ─── Small shared components ──────────────────────────────────────────────────
function AgeCategoryBadge({ code, large = false }) {
  if (!code) return null;
  const c = AGE_CAT_COLORS[code] || AGE_CAT_COLORS["Adult"];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: large ? 6 : 4, padding: large ? "5px 14px" : "3px 10px", borderRadius: 20, fontSize: large ? 13 : 11, fontWeight: 800, background: c.bg, border: `1.5px solid ${c.border}`, color: c.text, letterSpacing: "0.03em" }}>
      <FaBirthdayCake size={large ? 12 : 9} /> {code}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "10px", margin: "6px 0 0" }}>
      <div style={{ height: "2px", width: "18px", background: ST.yellow, borderRadius: "2px" }} />
      <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: "#8899aa" }}>{children}</span>
      <div style={{ flex: 1, height: "1px", background: "rgba(0,53,128,0.08)" }} />
    </div>
  );
}

function Field({ label, icon, name, type = "text", value, onChange, maxLength, error, placeholder, readOnly }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>{label}</label>
      <div style={{ ...inputWrapSt(T), border: error ? "1.5px solid #c0392b" : `1.5px solid ${T.inputBdr}`, ...(readOnly ? { background: "rgba(240,244,255,0.7)" } : {}) }}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <input type={type} name={name} value={value} onChange={onChange} style={{ ...inputSt(T), ...(readOnly ? { color: "#6a7a8a", cursor: "default" } : {}) }} maxLength={maxLength} placeholder={placeholder} readOnly={readOnly} />
      </div>
      {error && <p style={{ color: "#c0392b", fontSize: "11px", margin: "5px 0 0", display: "flex", alignItems: "center", gap: "4px" }}><FaExclamationCircle size={10} /> {error}</p>}
    </div>
  );
}

function SelectField({ label, icon, name, value, onChange, options, error }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>{label}</label>
      <div style={{ ...inputWrapSt(T), border: error ? "1.5px solid #c0392b" : `1.5px solid ${T.inputBdr}` }}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <select name={name} value={value} onChange={onChange} style={{ ...inputSt(T), cursor: "pointer", colorScheme: isDark ? "dark" : "light" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {error && <p style={{ color: "#c0392b", fontSize: "11px", margin: "5px 0 0" }}>{error}</p>}
    </div>
  );
}

function PlayingRoleSelect({ name, value, onChange, rolesByGroup }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>Playing Role</label>
      <div style={inputWrapSt(T)}>
        <FaBaseballBall style={{ marginRight: 10, color: T.textMuted, flexShrink: 0 }} />
        <select name={name} value={value} onChange={onChange} style={{ ...inputSt(T), cursor: "pointer", colorScheme: isDark ? "dark" : "light" }}>
          <option value="">Select playing role</option>
          {Object.entries(rolesByGroup).map(([grp, roles]) => (
            <optgroup key={grp} label={grp}>
              {roles.map(r => <option key={r.role_id} value={String(r.role_id)}>{r.role_name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}

function AgeCategoryField({ dobStr }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const ageCategories = useAgeCategories();
  const code = computeAgeCategory(dobStr, ageCategories);
  return (
    <div>
      <label style={labelSt(T)}>Age Category <span style={{ ...lightText(T), fontSize: 10 }}>(auto from DOB)</span></label>
      <div style={{ ...inputWrapSt(T), background: "rgba(240,244,255,0.7)", gap: 10 }}>
        <FaBirthdayCake style={{ color: T.textMuted, flexShrink: 0 }} />
        {code ? <AgeCategoryBadge code={code} /> : <span style={{ color: "#aab", fontSize: 13 }}>Enter date of birth above</span>}
      </div>
    </div>
  );
}

// ─── Student Info Header Card ─────────────────────────────────────────────────
function StudentInfoHeader({ student }) {
  const ageCategories = useAgeCategories();
  const { config } = useAppConfig();
  const { isDark } = useTheme(); const T = getT(isDark);
  if (!student) return null;
  const ageCode     = computeAgeCategory(student.date_of_birth, ageCategories);
  const statusLabel = student.active_status === 1 ? "Active" : student.active_status === 2 ? "Suspended" : "Cancelled";
  const statusColor = student.active_status === 1 ? ST.green : student.active_status === 2 ? ST.orange : ST.red;
  const statusBg    = student.active_status === 1 ? T.greenBg : student.active_status === 2 ? T.orangeBg : T.redBg;
  return (
    <div style={{ background: "linear-gradient(135deg,rgba(3,78,123,0.06),rgba(247,206,11,0.06))", border: "1.5px solid rgba(3,78,123,0.14)", borderRadius: 14, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ width: 54, height: 54, borderRadius: "50%", flexShrink: 0, background: `linear-gradient(135deg,${ST.yellow},#fff3a0)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 10px rgba(255,200,0,0.28)", overflow: "hidden" }}>
        {student.photo_url ? <img src={`${BASE}/${student.photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <FaUser size={22} color={ST.blue} />}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 7 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: ST.blue }}>{student.first_name} {student.last_name}</span>
          {ageCode && <AgeCategoryBadge code={ageCode} large />}
          <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: statusBg, color: statusColor, border: `1.5px solid ${statusColor}44` }}>{statusLabel}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 18px", fontSize: 12, color: "#6a7a8a" }}>
          {student.student_code && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaIdCard size={11} /> ID: <strong style={{ color: ST.blue }}>{student.student_code}</strong></span>}
          {student.gender && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaTransgender size={11} /> {student.gender}</span>}
          {student.date_of_birth && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaBirthdayCake size={11} /> {fmtDate(student.date_of_birth, config?.date_format)}</span>}
          {student.level_name && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, color: student.color_hex || ST.blue, background: (student.color_hex || ST.blue) + "18", border: `1px solid ${(student.color_hex || ST.blue)}44` }}><FaGraduationCap size={9} /> {student.level_name}</span>}
          {student.role_name && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaBaseballBall size={10} /> {student.role_name}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── SEARCH BAR ───────────────────────────────────────────────────────────────
function StudentSearchBar({ onSelect, displayName }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [search, setSearch]   = useState("");
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  const reqIdRef     = useRef(0);

  // When parent sets a student (e.g. after creation), reflect their name in the bar
  useEffect(() => {
    if (displayName !== undefined) { setSearch(displayName || ""); setResults([]); }
  }, [displayName]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const doSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${BASE}/students/search?name=${encodeURIComponent(val)}`);
        const d = await r.json();
        if (myReqId !== reqIdRef.current) return; // a newer keystroke already superseded this request
        setResults(d.students || []);
      } catch { if (myReqId === reqIdRef.current) setResults([]); }
    }, 300);
  };

  const pick = (s) => {
    setSearch(`${s.first_name} ${s.last_name}`);
    setResults([]);
    onSelect(s.student_id, `${s.first_name} ${s.last_name}`);
  };

  return (
    <div style={{ position: "relative", maxWidth: 520 }}>
      <label style={labelSt(T)}>Search Student</label>
      <div style={{ ...inputWrapSt(T), background: T.isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.90)" }}>
        <FaSearch style={{ marginRight: "10px", color: T.textMuted }} />
        <input type="text" value={search} onChange={e => doSearch(e.target.value)} placeholder="Type student name…" style={inputSt(T)} />
        {search && <FaTimes style={{ color: T.textMuted, cursor: "pointer", flexShrink: 0 }} onClick={() => { setSearch(""); setResults([]); onSelect(null); }} />}
      </div>
      {results.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%", background: T.isDark ? "rgba(10,20,40,0.97)" : "rgba(255,255,255,0.97)", backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, border: T.glassBdr, borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,40,100,0.18)", zIndex: 30, maxHeight: "220px", overflowY: "auto" }}>
          {results.map((s, i) => (
            <div key={s.student_id} onClick={() => pick(s)} style={{ padding: "11px 16px", cursor: "pointer", fontSize: "14px", borderBottom: i < results.length - 1 ? `1px solid ${T.glassBdr}` : "none", color: T.textPrimary, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,200,0,0.14)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span>{s.first_name} {s.last_name}</span>
              {s.age_category_code && <AgeCategoryBadge code={s.age_category_code} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── INLINE SEARCH BAR (no label, fits inside header row) ────────────────────
function InlineSearchBar({ onSelect, displayName }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [search, setSearch]   = useState("");
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  const reqIdRef     = useRef(0);

  useEffect(() => {
    if (displayName !== undefined) { setSearch(displayName || ""); setResults([]); }
  }, [displayName]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const doSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${BASE}/students/search?name=${encodeURIComponent(val)}`);
        const d = await r.json();
        if (myReqId !== reqIdRef.current) return;
        setResults(d.students || []);
      } catch { if (myReqId === reqIdRef.current) setResults([]); }
    }, 300);
  };

  const pick = (s) => {
    setSearch(`${s.first_name} ${s.last_name}`);
    setResults([]);
    onSelect(s.student_id, `${s.first_name} ${s.last_name}`);
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "0 12px", height: 38, borderRadius: 10,
        background: isDark ? "rgba(255,255,255,0.08)" : "#fff",
        border: `1.5px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)"}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <FaSearch style={{ color: T.textMuted, flexShrink: 0, fontSize: 13 }} />
        <input
          type="text" value={search}
          onChange={e => doSearch(e.target.value)}
          placeholder="Search student by name…"
          style={{ flex: 1, border: "none", outline: "none", background: "transparent",
            fontSize: 13, color: T.textPrimary, fontFamily: "inherit" }}
        />
        {search && (
          <FaTimes style={{ color: T.textMuted, cursor: "pointer", flexShrink: 0, fontSize: 12 }}
            onClick={() => { setSearch(""); setResults([]); onSelect(null); }} />
        )}
      </div>
      {results.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%",
          background: isDark ? "rgba(10,20,40,0.97)" : "rgba(255,255,255,0.97)",
          backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
          border: T.glassBdr, borderRadius: 12, boxShadow: "0 8px 24px rgba(0,40,100,0.18)",
          zIndex: 100, maxHeight: 220, overflowY: "auto" }}>
          {results.map((s, i) => (
            <div key={s.student_id} onClick={() => pick(s)}
              style={{ padding: "10px 16px", cursor: "pointer", fontSize: 13,
                borderBottom: i < results.length - 1 ? `1px solid ${T.glassBdr}` : "none",
                color: T.textPrimary, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,200,0,0.14)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span>{s.first_name} {s.last_name}</span>
              {s.age_category_code && <AgeCategoryBadge code={s.age_category_code} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DETAILS TAB (edit form for a selected student) ───────────────────────────
function DetailsTab({ studentId, studentInfo, setStudentInfo, currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const today   = new Date().toISOString().split("T")[0];
  const fileRef = useRef(null);
  const [form,         setForm]         = useState(blankForm(today));
  const [errors,       setErrors]       = useState({});
  const [msg,          setMsg]          = useState(null);
  const [skillLevels,  setSkillLevels]  = useState([]);
  const [playingRoles, setPlayingRoles] = useState([]);
  const [loaded,       setLoaded]       = useState(false);

  useEffect(() => {
    fetch(`${BASE}/students/skill-levels`).then(r => r.json()).then(d => setSkillLevels(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/students/playing-roles`).then(r => r.json()).then(d => setPlayingRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!studentId) return;
    setLoaded(false); setMsg(null);
    fetch(`${BASE}/students/${studentId}`).then(r => r.json()).then(d => {
      const s = d.student;
      setStudentInfo(s);
      setForm({
        first_name: s.first_name, last_name: s.last_name, date_of_birth: s.date_of_birth,
        gender: s.gender, parent_name: s.parent_name, parent_email: s.parent_email,
        parent_phone: formatPhone(s.parent_phone || ""), join_date: s.join_date,
        street_address: s.street_address || "", city: s.city || "",
        state: s.state || "", zip_code: s.zip_code || "",
        active_status: s.active_status, skill_level_id: s.skill_level_id ? String(s.skill_level_id) : "",
        playing_role_id: s.playing_role_id ? String(s.playing_role_id) : "",
        cricclub_player_id: s.cricclub_player_id || "",
        tournament_ready: Boolean(s.tournament_ready),
        jersey_purchased_date: s.jersey_purchased_date || "",
        existing_photo_url: s.photo_url || "", photo_file: null,
      });
      setLoaded(true);
    }).catch(() => {});
  }, [studentId]); // eslint-disable-line

  const rolesByGroup = playingRoles.reduce((acc, r) => { (acc[r.role_group] = acc[r.role_group] || []).push(r); return acc; }, {});

  const handleChange = (e) => {
    const { name, value, files, checked } = e.target;
    if (name === "photo_file")    { setForm(f => ({ ...f, photo_file: files?.[0] || null })); return; }
    if (name === "tournament_ready") { setForm(f => ({ ...f, tournament_ready: checked })); return; }
    if (name === "parent_phone")  { const pf = formatPhone(value); setForm(f => ({ ...f, parent_phone: pf })); return; }
    setForm(f => ({ ...f, [name]: value }));
    if (name === "date_of_birth") setStudentInfo(prev => prev ? { ...prev, date_of_birth: value } : prev);
  };

  const validate = () => {
    const err = {};
    if (form.cricclub_player_id && form.cricclub_player_id.length > 15) err.cricclub_player_id = "Max 15 characters";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "photo_file") return;
      if (k === "tournament_ready") { payload.append(k, v ? "true" : "false"); return; }
      if (k === "parent_phone") payload.append(k, v.replace(/\D/g, ""));
      else payload.append(k, v ?? "");
    });
    payload.append("updated_by", currentUserId || "admin");
    if (form.photo_file) payload.append("photo_file", form.photo_file);
    try {
      const res = await fetch(`${BASE}/students/${studentId}`, { method: "PUT", body: payload });
      const d   = await res.json();
      if (d.success) {
        setMsg({ ok: true, text: "Student updated successfully!" });
        const r2 = await fetch(`${BASE}/students/${studentId}`);
        const d2 = await r2.json();
        if (d2.student) setStudentInfo(d2.student);
      } else setMsg({ ok: false, text: d.message || "Error updating student" });
    } catch { setMsg({ ok: false, text: "Network error" }); }
  };

  if (!loaded) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 14 }}>Loading student details…</div>;

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}
      <form onSubmit={handleUpdate} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px" }}>
        <SectionLabel>Student Info</SectionLabel>
        <Field label="First Name" icon={<FaUser />} name="first_name" value={form.first_name} onChange={handleChange} />
        <Field label="Last Name"  icon={<FaUser />} name="last_name"  value={form.last_name}  onChange={handleChange} />
        <Field label={<>Date of Birth <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaCalendar />} name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} />
        <SelectField label="Gender" icon={<FaTransgender />} name="gender" value={form.gender} onChange={handleChange} options={[["", "Select gender"], ["Male", "Male"], ["Female", "Female"], ["Other", "Other"]]} />
        <Field label={<>Join Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaCalendar />} name="join_date" type="date" value={form.join_date} onChange={handleChange} />
        <SelectField label="Skill Level" icon={<FaGraduationCap />} name="skill_level_id" value={form.skill_level_id} onChange={handleChange} options={[["", "Select skill level"], ...skillLevels.map(l => [String(l.level_id), l.level_name])]} />
        <AgeCategoryField dobStr={form.date_of_birth} />
        <PlayingRoleSelect name="playing_role_id" value={form.playing_role_id} onChange={handleChange} rolesByGroup={rolesByGroup} />
        <Field label={<>CricClub Player ID <span style={lightText(T)}>(max 15)</span></>} icon={<FaShieldAlt />} name="cricclub_player_id" value={form.cricclub_player_id} onChange={handleChange} maxLength={15} placeholder="e.g. CC1234567890123" error={errors.cricclub_player_id} />
        <SelectField label="Active Status" name="active_status" value={form.active_status} onChange={handleChange} options={[[1, "Active"], [2, "Suspended"], [3, "Deleted"]]} />

        <SectionLabel>Tournament &amp; Jersey</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <label style={labelSt(T)}>Tournament Ready</label>
          <div onClick={() => setForm(f => ({ ...f, tournament_ready: !f.tournament_ready }))} style={{ ...inputWrapSt(T), gap: "12px", cursor: "pointer", border: form.tournament_ready ? "1.5px solid rgba(5,150,105,0.55)" : `1.5px solid ${T.inputBdr}`, background: form.tournament_ready ? "rgba(5,150,105,0.07)" : T.inputBg }}>
            <FaTrophy style={{ color: form.tournament_ready ? ST.green : T.textMuted, flexShrink: 0 }} />
            <input type="checkbox" name="tournament_ready" checked={form.tournament_ready} onChange={handleChange} style={{ accentColor: ST.green, width: "16px", height: "16px", cursor: "pointer" }} onClick={e => e.stopPropagation()} />
            <span style={{ fontSize: "14px", color: form.tournament_ready ? ST.green : T.textSub, fontWeight: form.tournament_ready ? 700 : 500 }}>{form.tournament_ready ? "Yes — tournament eligible" : "Not yet tournament ready"}</span>
          </div>
        </div>
        <Field label={<>Jersey Purchased Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaTshirt />} name="jersey_purchased_date" type="date" value={form.jersey_purchased_date} onChange={handleChange} />

        <SectionLabel>Parent / Guardian</SectionLabel>
        <Field label={<>Parent Name <span style={lightText(T)}>(First & Last)</span></>} icon={<FaUserFriends />} name="parent_name" value={form.parent_name} onChange={handleChange} />
        <Field label="Parent Email" icon={<FaEnvelope />} name="parent_email" value={form.parent_email} onChange={handleChange} error={errors.parent_email} />
        <Field label={<>Phone <span style={lightText(T)}>(xxx-xxx-xxxx)</span></>} icon={<FaPhone />} name="parent_phone" value={form.parent_phone} onChange={handleChange} maxLength={12} />

        <SectionLabel>Home Address</SectionLabel>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Street Address" icon={<FaMapMarkerAlt />} name="street_address" value={form.street_address} onChange={handleChange} placeholder="e.g. 12 Main Street" />
        </div>
        <Field label="City" icon={<FaMapMarkerAlt />} name="city" value={form.city} onChange={handleChange} placeholder="e.g. Sydney" />
        <Field label="State" icon={<FaMapMarkerAlt />} name="state" value={form.state} onChange={handleChange} placeholder="e.g. NSW" />
        <Field label="Zip / Postal Code" icon={<FaMapMarkerAlt />} name="zip_code" value={form.zip_code} onChange={handleChange} placeholder="e.g. 2000" />
        <div />{/* grid spacer */}

        <div>
          <label style={labelSt(T)}>Upload New Photo (optional)</label>
          <div style={inputWrapSt(T)}>
            <FaImage style={{ marginRight: "10px", color: T.textMuted, flexShrink: 0 }} />
            <input type="file" name="photo_file" accept="image/*" onChange={handleChange} style={{ fontSize: "13px" }} ref={fileRef} />
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
          <PrimaryButton type="submit" style={{ width: "100%", padding: "13px" }}><FaSave size={15} /> Update Student</PrimaryButton>
        </div>
      </form>
    </div>
  );
}

// ─── COACHING PROGRAM TAB ─────────────────────────────────────────────────────
const BILLING_CYCLES = [
  { id: "monthly",     label: "Monthly",     mult: 1  },
  { id: "quarterly",   label: "Quarterly",   mult: 3  },
  { id: "semi_annual", label: "Semi-Annual", mult: 6  },
  { id: "annual",      label: "Annual",      mult: 12 },
];

function applyStudentDisc(base, discAmt, discPct) {
  let net = parseFloat(base) || 0;
  if (discPct  && parseFloat(discPct)  > 0) net = net * (1 - parseFloat(discPct) / 100);
  if (discAmt  && parseFloat(discAmt)  > 0) net = net - parseFloat(discAmt);
  return Math.max(0, net);
}

const DAY_NAMES  = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const DAY_COLORS = ["#2563eb","#7c3aed",ST.orange,"#0ea5e9",ST.green,ST.red,"#64748b"];

function CoachingProgramTab({ studentId, currentUserId, onNext }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const today    = new Date().toISOString().slice(0, 10);
  const thisYear = String(new Date().getFullYear());

  const [programs,       setPrograms]       = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]); // ALL batch enrollments for list view
  const [enrollment,     setEnrollment]     = useState(null); // primary (first) for billing section
  const [loading,        setLoading]        = useState(true);
  const [msg,            setMsg]            = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [editMode,       setEditMode]       = useState(false);

  // Form fields
  const [selYear,    setSelYear]    = useState(thisYear);
  const [selProg,    setSelProg]    = useState("");
  const [cycle,      setCycle]      = useState("monthly");
  const [discAmt,    setDiscAmt]    = useState("");
  const [discPct,    setDiscPct]    = useState("");
  const [discFrom,   setDiscFrom]   = useState(today);
  const [discTo,     setDiscTo]     = useState("");
  const [discReason, setDiscReason] = useState("");
  const [discErr,    setDiscErr]    = useState("");
  // Placeholder for additional (one-time or recurring) fees, e.g. a
  // tournament fee — editable so admin can add or remove one without a full
  // program change. Pre-filled from whatever's already on the student's
  // record so a save never silently drops it (that was the actual bug).
  const [extraFees,    setExtraFees]    = useState([]);
  const [newFeeLabel,  setNewFeeLabel]  = useState("");
  const [newFeeAmt,    setNewFeeAmt]    = useState("");
  const [newFeeRecur,  setNewFeeRecur]  = useState(false);
  const [history,    setHistory]    = useState([]);

  const loadHistory = useCallback(() => {
    fetch(`${BASE}/student-billing/${studentId}/program-history`)
      .then(r => r.json())
      .then(d => setHistory(d.history || []))
      .catch(() => setHistory([]));
  }, [studentId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const load = useCallback(async () => {
    setLoading(true);
    const [progRes, enrRes] = await Promise.allSettled([
      fetch(`${BASE}/coaching-programs/`).then(r => r.json()),
      fetch(`${BASE}/student-billing/${studentId}/coaching-program`).then(r => r.json()),
    ]);
    const progs = progRes.status === "fulfilled" ? (progRes.value.programs || []) : [];
    const data  = enrRes.status  === "fulfilled" ? enrRes.value : {};
    setPrograms(progs);
    setAllEnrollments(data.enrollments || []);
    setEnrollment(data.billing || null);
    setLoading(false);
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // Auto-open edit mode when loading completes and there is no enrollment yet
  useEffect(() => {
    if (!loading && enrollment === null) {
      setEditMode(true);
    }
  }, [loading, enrollment]);

  // Unique years from programs list, descending
  const progYears = [...new Set(programs.map(p => (p.valid_from || "").slice(0, 4)).filter(Boolean))].sort((a, b) => b - a);
  const filteredProgs = programs.filter(p => (p.valid_from || "").slice(0, 4) === selYear);

  const openEdit = () => {
    const e = enrollment;
    const progYear = (e?.prog_valid_from || "").slice(0, 4);
    setSelYear(progYears.includes(progYear) ? progYear : (progYears[0] || thisYear));
    setSelProg(e?.program_id ? String(e.program_id) : "");
    setCycle(e?.billing_cycle || "monthly");
    setDiscAmt(e?.discount_amount ? String(e.discount_amount) : "");
    setDiscPct(e?.discount_pct   ? String(e.discount_pct)    : "");
    setDiscFrom(e?.discount_valid_from || today);
    setDiscTo(e?.discount_valid_to || "");
    setDiscReason(e?.discount_reason || "");
    setExtraFees((e?.extra_fees || []).map((f, i) => ({ ...f, id: f.id || Date.now() + i })));
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
    setDiscErr(""); setMsg(null);
    setEditMode(true);
  };

  const addExtraFee = () => {
    if (!newFeeLabel.trim() || !newFeeAmt) return;
    setExtraFees(f => [...f, { id: Date.now(), label: newFeeLabel.trim(), amount: parseFloat(newFeeAmt), recurring: newFeeRecur }]);
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
  };
  const removeExtraFee = (id) => setExtraFees(f => f.filter(x => x.id !== id));

  const handleSave = async () => {
    if (discAmt && parseFloat(discAmt) > 0 && discPct && parseFloat(discPct) > 0) {
      setDiscErr("Use either $ discount or % discount — not both."); return;
    }
    setSaving(true); setMsg(null);
    try {
      const payload = {
        program_id:          selProg ? parseInt(selProg) : null,
        billing_cycle:       cycle,
        discount_amount:     discAmt && parseFloat(discAmt) > 0 ? discAmt : null,
        discount_pct:        discPct && parseFloat(discPct) > 0 ? discPct : null,
        discount_valid_from: discFrom || null,
        discount_valid_to:   discTo   || null,
        discount_reason:     discReason || null,
        extra_fees:          extraFees.map(({ id, ...f }) => f),
        updated_by:          currentUserId || "admin",
      };
      const r = await fetch(
        `${BASE}/student-billing/${studentId}/coaching-program`,
        { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
      );
      const d = await r.json();
      if (d.success) {
        const sync = d.synced_schedule;
        let text = sync
          ? `Saved. Recurring schedule updated to ${CYCLE_LABELS[sync.frequency] || sync.frequency} — $${Number(sync.amount).toFixed(2)}.`
          : "Saved successfully.";
        if (d.program_change) {
          const pc = d.program_change;
          text = `Program changed. Credited $${Number(pc.credit_refunded).toFixed(2)} for the unused old program `
               + `(admin/jersey/tournament fees excluded), charged $${Number(pc.new_program_charged).toFixed(2)} prorated `
               + `for the new program. ${sync ? `Recurring schedule now $${Number(sync.amount).toFixed(2)}/${CYCLE_LABELS[sync.frequency] || sync.frequency}.` : ""}`;
        }
        setMsg({ ok: true, text });
        setEditMode(false); await load(); loadHistory();
      }
      else setMsg({ ok: false, text: d.message || "Save failed." });
    } catch { setMsg({ ok: false, text: "Network error." }); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 28, textAlign: "center", color: T.textMuted, fontSize: 13 }}>Loading…</div>;

  const computeFees = (srcRow, da, dp) => BILLING_CYCLES.map(cyc => {
    const fk  = cyc.id === "monthly" ? "fee_monthly"       : cyc.id === "quarterly" ? "fee_quarterly"       : cyc.id === "semi_annual" ? "fee_semi_annual"  : "fee_annual";
    const dtk = cyc.id === "monthly" ? "disc_monthly_type" : cyc.id === "quarterly" ? "disc_quarterly_type" : cyc.id === "semi_annual" ? "disc_semi_type"   : "disc_annual_type";
    const dvk = cyc.id === "monthly" ? "disc_monthly_value": cyc.id === "quarterly" ? "disc_quarterly_value": cyc.id === "semi_annual" ? "disc_semi_value"  : "disc_annual_value";
    const base = parseFloat(srcRow?.[fk]) || 0;
    if (!base) return null;
    const dt = srcRow?.[dtk]; const dv = parseFloat(srcRow?.[dvk]) || 0;
    const afterProg    = dt === "pct" && dv ? base * (1 - dv / 100) : dt === "amt" && dv ? base - dv : base;
    const afterStudent = applyStudentDisc(afterProg, da, dp);
    return { ...cyc, base, afterProg, afterStudent, progSave: base - afterProg, studentSave: afterProg - afterStudent, totalSave: base - afterStudent };
  }).filter(Boolean);

  const editProg   = programs.find(p => p.program_id === parseInt(selProg));
  const previewSrc = editMode ? editProg : enrollment;
  const previewDa  = editMode ? discAmt  : enrollment?.discount_amount;
  const previewDp  = editMode ? discPct  : enrollment?.discount_pct;
  const fees       = previewSrc ? computeFees(previewSrc, previewDa, previewDp) : [];
  const activeCyc  = editMode ? cycle : (enrollment?.billing_cycle || "monthly");
  const activeFee  = fees.find(f => f.id === activeCyc) || fees[0];

  const G = T.glassBdr;
  const gCard = (children) => (
    <div style={{ background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, border: G, borderRadius: 12, padding: "12px 16px" }}>{children}</div>
  );
  const secHead = (t, sub) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: ST.blue }}>{t}</div>
      {sub && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{sub}</div>}
    </div>
  );
  const inp = { padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${T.inputBdr || "rgba(0,53,128,0.13)"}`, background: T.inputBg || (isDark ? "rgba(255,255,255,0.07)" : "#fff"), fontSize: 13, color: T.textPrimary, outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      {/* ══ SECTION 1: COACHING PROGRAM & BILLING ══ */}
      {gCard(
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            {secHead("Coaching Program & Billing", "Program billed — can override batch default")}
            {!editMode && (
              <GhostButton onClick={openEdit} style={{ padding: "5px 12px", fontSize: 11, flexShrink: 0 }}>
                <FaPencilAlt size={9} /> Edit
              </GhostButton>
            )}
          </div>

          {editMode ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

              {/* ── Year + Program row ── */}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ width: 90, flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLabel, marginBottom: 4 }}>Year</div>
                  <select value={selYear} onChange={e => { setSelYear(e.target.value); setSelProg(""); }} style={inp}>
                    {progYears.length === 0 && <option value={thisYear}>{thisYear}</option>}
                    {progYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLabel, marginBottom: 4 }}>Coaching Program</div>
                  <select value={selProg} onChange={e => setSelProg(e.target.value)} style={inp}>
                    <option value="">— Select program —</option>
                    {filteredProgs.map(p => (
                      <option key={p.program_id} value={p.program_id}>
                        {p.program_name}{p.level_name ? ` · ${p.level_name}` : ""}
                      </option>
                    ))}
                    {filteredProgs.length === 0 && <option disabled>No programs for {selYear}</option>}
                  </select>
                </div>
              </div>

              {/* ── Billing cycle compact pills ── */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLabel, marginBottom: 6 }}>Billing Cycle</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {BILLING_CYCLES.map(cyc => {
                    const active = cycle === cyc.id;
                    const f = fees.find(x => x.id === cyc.id);
                    return (
                      <div key={cyc.id} onClick={() => setCycle(cyc.id)} style={{
                        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                        padding: "6px 8px", borderRadius: 8, cursor: "pointer", textAlign: "center",
                        background: active ? ST.yellowGrad : T.glass,
                        border: active ? "1.5px solid rgba(255,200,0,0.5)" : G,
                        color: active ? ST.blueDark : T.textPrimary,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{cyc.label}</span>
                        {f && <span style={{ fontSize: 12, fontWeight: 800, color: active ? ST.blueDark : ST.blue, marginTop: 2 }}>${f.afterStudent.toFixed(2)}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Discount fields ── */}
              <div style={{ background: isDark ? "rgba(5,150,105,0.07)" : "rgba(5,150,105,0.04)", border: "1px solid rgba(5,150,105,0.18)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: ST.green, marginBottom: 8 }}>
                  Student Discount &nbsp;<span style={{ fontWeight: 400, textTransform: "none", fontSize: 9, color: T.textMuted }}>($  or  % — not both)</span>
                </div>
                {discErr && <div style={{ color: ST.red, fontSize: 11, marginBottom: 8, display: "flex", gap: 5, alignItems: "center" }}><FaExclamationCircle size={10}/> {discErr}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLabel, marginBottom: 3 }}>Discount ($)</div>
                    <input type="number" value={discAmt} placeholder="0.00"
                      onChange={e => { setDiscAmt(e.target.value); if (e.target.value) setDiscPct(""); setDiscErr(""); }} style={inp} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLabel, marginBottom: 3 }}>Discount (%)</div>
                    <input type="number" value={discPct} placeholder="0.0"
                      onChange={e => { setDiscPct(e.target.value); if (e.target.value) setDiscAmt(""); setDiscErr(""); }} style={inp} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLabel, marginBottom: 3 }}>Valid From</div>
                    <input type="date" value={discFrom} onChange={e => setDiscFrom(e.target.value)} style={inp} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLabel, marginBottom: 3 }}>Valid To</div>
                    <input type="date" value={discTo} onChange={e => setDiscTo(e.target.value)} style={inp} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLabel, marginBottom: 3 }}>Reason</div>
                  <input type="text" value={discReason} placeholder="e.g. Sibling discount, scholarship, trial period…"
                    onChange={e => setDiscReason(e.target.value)} style={inp} />
                </div>
              </div>

              {/* ── Additional Fees placeholder — one-time or recurring
                    (e.g. a tournament fee), editable so admin can remove
                    one as needed instead of it only ever being set once at
                    approval with no way back in. Persists across program
                    and billing-cycle changes since it's now driven by this
                    state, not silently re-derived from a stale value. ── */}
              <div style={{ border: `1px solid ${G}`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: ST.blue, marginBottom: 8 }}>
                  Additional Fees {extraFees.length === 0 && <span style={{ fontWeight: 400, textTransform: "none", color: T.textMuted }}>— none applied</span>}
                </div>
                {extraFees.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                    {extraFees.map(f => (
                      <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, fontSize: 12, color: T.textPrimary }}>
                          {f.label} — ${parseFloat(f.amount).toFixed(2)} {f.recurring ? "(recurring, monthly)" : "(one-time)"}
                        </span>
                        <button onClick={() => removeExtraFee(f.id)}
                          style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer", padding: 4 }}
                          title="Remove this fee">
                          <FaTrash size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px auto", gap: 8, alignItems: "center" }}>
                  <input placeholder="Fee label (e.g. Tournament Fee)" value={newFeeLabel} onChange={e => setNewFeeLabel(e.target.value)} style={inp} />
                  <input type="number" min="0" step="0.01" placeholder="Amount" value={newFeeAmt} onChange={e => setNewFeeAmt(e.target.value)} style={inp} />
                  <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textSub }}>
                    <input type="checkbox" checked={newFeeRecur} onChange={e => setNewFeeRecur(e.target.checked)} /> Recurring
                  </label>
                  <GhostButton onClick={addExtraFee} style={{ padding: "6px 10px", fontSize: 11 }}><FaPlus size={9} /></GhostButton>
                </div>
              </div>

              {/* ── Fee preview table ── */}
              {fees.length > 0 && (
                <div style={{ background: isDark ? "rgba(3,78,123,0.07)" : "rgba(3,78,123,0.03)", border: "1px solid rgba(3,78,123,0.12)", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: ST.blue, marginBottom: 8 }}>Fee Preview</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead><tr>
                      {["Cycle","Base","Prog.Disc","Stu.Disc","Total"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "3px 6px", fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${G}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{fees.map(r => (
                      <tr key={r.id} style={{ background: cycle === r.id ? (isDark ? "rgba(247,206,11,0.08)" : "rgba(247,206,11,0.1)") : "transparent" }}>
                        <td style={{ padding: "4px 6px", fontWeight: cycle === r.id ? 700 : 500, color: cycle === r.id ? ST.blue : T.textPrimary }}>{r.label}</td>
                        <td style={{ padding: "4px 6px", color: T.textMuted }}>${r.base.toFixed(2)}</td>
                        <td style={{ padding: "4px 6px", color: r.progSave > 0 ? ST.green : T.textMuted }}>{r.progSave > 0 ? `-$${r.progSave.toFixed(2)}` : "—"}</td>
                        <td style={{ padding: "4px 6px", color: r.studentSave > 0 ? ST.green : T.textMuted }}>{r.studentSave > 0 ? `-$${r.studentSave.toFixed(2)}` : "—"}</td>
                        <td style={{ padding: "4px 6px", fontWeight: 800, color: ST.blue }}>${r.afterStudent.toFixed(2)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <PrimaryButton onClick={handleSave} disabled={saving} size="sm" style={{ padding: "8px 16px" }}>
                  <FaSave size={12} /> {saving ? "Saving…" : "Save"}
                </PrimaryButton>
                <GhostButton onClick={() => { setEditMode(false); setMsg(null); }} style={{ padding: "8px 14px", fontSize: 12 }}>
                  <FaTimesCircle size={12} /> Cancel
                </GhostButton>
              </div>
            </div>

          ) : enrollment ? (
            /* ── READ VIEW ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: ST.blue }}>{enrollment.program_name || "—"}</span>
                {enrollment.level_name && (
                  <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                    background: `${enrollment.level_color||"#94a3b8"}20`, color: enrollment.level_color||"#64748b",
                    border: `1px solid ${enrollment.level_color||"#94a3b8"}50` }}>{enrollment.level_name}</span>
                )}
                {enrollment.prog_valid_from && (
                  <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: "rgba(3,78,123,0.08)", color: ST.blue }}>
                    {enrollment.prog_valid_from.slice(0,4)}
                  </span>
                )}
                <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: ST.yellowGrad, color: ST.blueDark }}>
                  {BILLING_CYCLES.find(c => c.id === (enrollment.billing_cycle||"monthly"))?.label || "Monthly"}
                </span>
              </div>

              {activeFee && (
                <div style={{ background: isDark ? "rgba(3,78,123,0.07)" : "rgba(3,78,123,0.03)", border: "1px solid rgba(3,78,123,0.12)", borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: T.textMuted, marginBottom: 6 }}>Billing — {activeFee.label}</div>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 9, color: T.textMuted, marginBottom: 1 }}>Program Cost</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: activeFee.totalSave > 0 ? T.textMuted : T.textPrimary,
                        textDecoration: activeFee.totalSave > 0 ? "line-through" : "none", opacity: activeFee.totalSave > 0 ? 0.45 : 1 }}>
                        ${activeFee.base.toFixed(2)}
                      </div>
                    </div>
                    {activeFee.progSave > 0 && <>
                      <div style={{ color: T.textMuted, alignSelf: "center", fontSize: 14 }}>−</div>
                      <div><div style={{ fontSize: 9, color: ST.green, marginBottom: 1 }}>Prog. Discount</div><div style={{ fontSize: 14, fontWeight: 700, color: ST.green }}>${activeFee.progSave.toFixed(2)}</div></div>
                    </>}
                    {activeFee.studentSave > 0 && <>
                      <div style={{ color: T.textMuted, alignSelf: "center", fontSize: 14 }}>−</div>
                      <div><div style={{ fontSize: 9, color: ST.green, marginBottom: 1 }}>Student Discount</div><div style={{ fontSize: 14, fontWeight: 700, color: ST.green }}>${activeFee.studentSave.toFixed(2)}</div></div>
                    </>}
                    <div style={{ color: T.textMuted, alignSelf: "center", fontSize: 14 }}>=</div>
                    <div><div style={{ fontSize: 9, color: ST.blue, fontWeight: 700, marginBottom: 1 }}>Total Billed</div><div style={{ fontSize: 20, fontWeight: 900, color: ST.blue }}>${activeFee.afterStudent.toFixed(2)}</div></div>
                    {activeFee.totalSave > 0 && <span style={{ alignSelf: "center", padding: "2px 8px", borderRadius: 20, background: "rgba(5,150,105,0.1)", color: ST.green, fontSize: 10, fontWeight: 700 }}>Save ${activeFee.totalSave.toFixed(2)}</span>}
                  </div>
                </div>
              )}

              {fees.length > 1 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
                  {fees.map(r => {
                    const isCur = r.id === (enrollment.billing_cycle || "monthly");
                    return (
                      <div key={r.id} style={{ padding: "7px 9px", borderRadius: 8,
                        background: isCur ? "linear-gradient(135deg,rgba(247,206,11,0.12),rgba(247,206,11,0.05))" : (isDark ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                        border: isCur ? "1.5px solid rgba(247,206,11,0.4)" : `1px solid ${G}` }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", marginBottom: 2 }}>{r.label}</div>
                        {r.base !== r.afterStudent && <div style={{ fontSize: 10, color: T.textMuted, textDecoration: "line-through" }}>${r.base.toFixed(2)}</div>}
                        <div style={{ fontSize: 12, fontWeight: 800, color: ST.blue }}>${r.afterStudent.toFixed(2)}</div>
                        {r.totalSave > 0 && <div style={{ fontSize: 9, color: ST.green }}>−${r.totalSave.toFixed(2)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {(enrollment.discount_amount || enrollment.discount_pct) && (
                <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", padding: "5px 10px",
                  background: "rgba(5,150,105,0.05)", border: "1px solid rgba(5,150,105,0.15)", borderRadius: 8 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: ST.green }}>Student Discount</span>
                  {enrollment.discount_pct    && <span style={{ fontSize: 11, fontWeight: 700, color: ST.green }}>{enrollment.discount_pct}% off</span>}
                  {enrollment.discount_amount && <span style={{ fontSize: 11, fontWeight: 700, color: ST.green }}>−${parseFloat(enrollment.discount_amount).toFixed(2)}</span>}
                  {enrollment.discount_reason && <span style={{ fontSize: 10, color: T.textMuted, fontStyle: "italic" }}>{enrollment.discount_reason}</span>}
                  {(enrollment.discount_valid_from||enrollment.discount_valid_to) && <span style={{ fontSize: 10, color: T.textMuted }}>{enrollment.discount_valid_from||"—"} → {enrollment.discount_valid_to||"—"}</span>}
                </div>
              )}
            </div>

          ) : (
            <div style={{ textAlign: "center", padding: "20px 16px", color: T.textMuted, fontSize: 12 }}>
              <FaGraduationCap size={22} style={{ marginBottom: 7, opacity: 0.25 }} />
              <div>No batch enrollment found.</div>
              <div style={{ fontSize: 11, marginTop: 3 }}>Enroll this student in a batch via <strong>Batch Management</strong>, then return here to configure billing.</div>
            </div>
          )}
        </div>
      )}

      {/* ══ SECTION 2: BATCH SCHEDULE — LIST VIEW ══ */}
      {gCard(
        <div>
          {secHead("Batch Schedule", `${allEnrollments.length} enrollment${allEnrollments.length !== 1 ? "s" : ""} — training schedule, separate from billing`)}
          {allEnrollments.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {allEnrollments.map(enr => (
                <div key={enr.enrollment_id} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "9px 11px",
                  borderRadius: 9, background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                  border: `1px solid ${G}`, flexWrap: "wrap"
                }}>
                  <div style={{ minWidth: 110, flex: "0 0 auto" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: ST.blue }}>{enr.batch_name}</div>
                    {(enr.enroll_date || enr.end_date) && (
                      <div style={{ fontSize: 9, color: T.textMuted, marginTop: 1 }}>{enr.enroll_date ? fmtDate(enr.enroll_date, dateFmt) : "?"} → {enr.end_date ? fmtDate(enr.end_date, dateFmt) : "ongoing"}</div>
                    )}
                  </div>
                  {enr.schedule_days?.length > 0 && (
                    <div style={{ display: "flex", gap: 3, flex: "0 0 auto" }}>
                      {DAY_NAMES.map((name, i) => {
                        if (!enr.schedule_days.includes(i)) return null;
                        return <span key={i} style={{ padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: DAY_COLORS[i], color: "#fff" }}>{name}</span>;
                      })}
                    </div>
                  )}
                  {enr.level_name && (
                    <span style={{ padding: "2px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700, flex: "0 0 auto",
                      background: `${enr.level_color||"#94a3b8"}18`, color: enr.level_color||"#64748b",
                      border: `1px solid ${enr.level_color||"#94a3b8"}44` }}>{enr.level_name}</span>
                  )}
                  {enr.weekday_start && <span style={{ fontSize: 10, color: T.textMuted, flex: "0 0 auto" }}>{enr.weekday_start} – {enr.weekday_end}</span>}
                  {enr.enrolled_on && <span style={{ fontSize: 10, color: T.textMuted, flex: "0 0 auto" }}>from <strong>{enr.enrolled_on.slice(0,10)}</strong></span>}
                  <span style={{
                    marginLeft: "auto", padding: "2px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, flex: "0 0 auto",
                    background: enr.enrollment_status === "Active" ? "rgba(5,150,105,0.1)" : "rgba(148,163,184,0.15)",
                    color: enr.enrollment_status === "Active" ? ST.green : "#64748b",
                    border: enr.enrollment_status === "Active" ? "1px solid rgba(5,150,105,0.25)" : "1px solid rgba(148,163,184,0.25)",
                  }}>● {enr.enrollment_status || "Active"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "18px 16px", color: T.textMuted, fontSize: 12 }}>
              No batch assignments. Go to <strong>Batch Management</strong> to assign this student to a training group.
            </div>
          )}
        </div>
      )}

      {history.length > 1 && gCard(
        <>
          {secHead("Program History", "Every coaching program — and, for a dual-role person, every lane membership — this student has been enrolled in.")}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {history.map(h => {
              const isCurrent = h.status === "active";
              const isLane = h.history_source === "lane_membership";
              return (
                <div key={`${h.history_source || "coaching_program"}-${h.history_id || h.membership_id}`} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6,
                  border: `1px solid ${isCurrent ? "rgba(16,185,129,0.3)" : G}`,
                  background: isCurrent ? "rgba(16,185,129,0.05)" : "transparent",
                  borderRadius: 9, padding: "8px 12px" }}>
                  <div>
                    {isLane && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 20, marginRight: 6,
                        background: "rgba(59,130,246,0.15)", color: "#3b82f6", textTransform: "uppercase" }}>Lane Membership</span>
                    )}
                    <span style={{ fontSize: 12, fontWeight: 800, color: isCurrent ? ST.green : T.text }}>{h.program_name || `Program #${h.program_id}`}</span>
                    <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 8 }}>
                      {fmtDate(h.start_date, dateFmt)} – {h.end_date ? fmtDate(h.end_date, dateFmt) : "present"}
                    </span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, textTransform: "capitalize",
                    background: isCurrent ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                    color: isCurrent ? ST.green : "#64748b" }}>{h.status}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {onNext && (
        <div style={{ paddingTop: 10, borderTop: `1px solid ${G}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>
            {enrollment ? "Program assigned. Next: set up the parent login." : "You can continue without a program assignment."}
          </div>
          <PrimaryButton onClick={onNext} size="sm" style={{ padding: "7px 14px" }}>Next: Parent Login <FaArrowRight size={10} /></PrimaryButton>
        </div>
      )}
    </div>
  );
}

// ─── LOGIN TAB (Parent Access) ────────────────────────────────────────────────
function LoginTab({ studentId, studentInfo, currentUserId, onNext }) {
  return <ParentAccessPanel currentUserId={currentUserId} studentId={studentId} studentInfo={studentInfo} onNext={onNext} />;
}

// ─── BILLING TAB ──────────────────────────────────────────────────────────────
function BillingTab({ studentId, currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [subTab, setSubTab] = useState("transactions");

  const subTabs = [
    { key: "transactions",     label: "All Transactions",  icon: <FaHistory size={12} /> },
    { key: "paid",             label: "Paid Fees",         icon: <FaCheckSquare size={12} /> },
    { key: "unpaid",           label: "Unpaid Fees",       icon: <FaExclamationCircle size={12} /> },
    { key: "payment_profile",  label: "Payment Profile",   icon: <FaCreditCard size={12} /> },
    { key: "recurring",        label: "Billing Cycle",     icon: <FaRegClock size={12} /> },
  ];

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {subTabs.map(st => (
          <button key={st.key} onClick={() => setSubTab(st.key)} style={{
            padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
            color: subTab === st.key ? ST.blueDark : T.textLabel,
            background: subTab === st.key ? ST.yellowGrad : T.glass,
            border: subTab === st.key ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
            backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
            boxShadow: subTab === st.key ? "0 3px 10px rgba(255,200,0,0.25)" : T.glassSh,
          }}>
            {st.icon} {st.label}
          </button>
        ))}
      </div>

      {subTab === "transactions"    && <TransactionsSubTab studentId={studentId} />}
      {subTab === "paid"            && <PaidFeesSubTab studentId={studentId} />}
      {subTab === "unpaid"          && <UnpaidFeesSubTab studentId={studentId} />}
      {subTab === "payment_profile" && <PaymentProfileSubTab studentId={studentId} currentUserId={currentUserId} />}
      {subTab === "recurring"       && <RecurringSubTab studentId={studentId} currentUserId={currentUserId} />}
    </div>
  );
}

const REFUNDABLE_STATUSES = ["paid", "confirmed", "completed", "checked_in"];

function RefundModal({ row, studentId, T, isDark, onClose, onDone }) {
  const [refundType, setRefundType] = useState("full");
  const [amount,     setAmount]     = useState("");
  const [reason,     setReason]     = useState("");
  const [working,    setWorking]    = useState(false);
  const [result,     setResult]     = useState(null);

  const totalCharged   = parseFloat(row.amount_due || 0);
  const alreadyRefunded = parseFloat(row.refunded_amount || 0);
  const applicable     = Math.max(0, totalCharged - alreadyRefunded);

  const inp = {
    width: "100%", boxSizing: "border-box", padding: "8px 11px",
    borderRadius: 8, fontSize: 13, outline: "none",
    background: isDark ? "rgba(255,255,255,0.07)" : "#f8fafc",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
    color: T.textPrimary,
  };

  const submit = async () => {
    const partialAmt = parseFloat(amount);
    if (refundType === "partial") {
      if (!amount || isNaN(partialAmt) || partialAmt <= 0 || partialAmt > applicable) {
        setResult({ ok: false, msg: `Enter a valid amount between $0.01 and $${applicable.toFixed(2)}` });
        return;
      }
    }
    if (!reason.trim()) { setResult({ ok: false, msg: "Please enter a reason for the refund." }); return; }
    setWorking(true); setResult(null);
    try {
      const endpoint = row.source_type === "oto_booking"
        ? `${BASE}/oto/bookings/${row.source_id}/refund`
        : `${BASE}/student-billing/${studentId}/charges/${row.source_id}/refund`;
      const res  = await fetch(endpoint, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refund_type: refundType, amount: refundType === "partial" ? partialAmt : null, reason }),
      });
      const data = await res.json();
      setResult({ ok: data.success, msg: data.message, refundId: data.refund_id,
                  refunded: data.refunded_amount, cancelled: data.cancelled });
      if (data.success) onDone();
    } catch { setResult({ ok: false, msg: "Network error — please try again." }); }
    setWorking(false);
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
  const box = { background: isDark ? "#1e293b" : "#fff", borderRadius: 14, padding: 28,
    width: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {result?.ok ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, color: T.textPrimary }}>
              {result.cancelled ? "Fully Refunded & Cancelled" : `Refund $${parseFloat(result.refunded || 0).toFixed(2)} Issued`}
            </div>
            <div style={{ fontSize: 12, color: ST.green, background: "rgba(5,150,105,0.08)",
              border: "1px solid rgba(5,150,105,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 18, textAlign: "left" }}>
              {result.msg}
              {result.refundId && <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 11 }}>Refund ID: {result.refundId}</div>}
            </div>
            <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: 8, border: "none",
              cursor: "pointer", background: ST.green, color: "#fff", fontWeight: 700, width: "100%" }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, marginBottom: 4 }}>Issue Refund</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
              {row.billing_ref || `Booking #${row.source_id}`}
            </div>

            {/* Charged / Refunded / Applicable summary */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>CHARGED</div>
                <div style={{ fontWeight: 800, color: T.textPrimary }}>${totalCharged.toFixed(2)}</div>
              </div>
              {alreadyRefunded > 0 && (
                <div style={{ flex: 1, background: "rgba(248,113,113,0.10)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: ST.red, marginBottom: 2 }}>REFUNDED</div>
                  <div style={{ fontWeight: 800, color: ST.red }}>${alreadyRefunded.toFixed(2)}</div>
                </div>
              )}
              <div style={{ flex: 1, background: "rgba(52,211,153,0.10)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: ST.green, marginBottom: 2 }}>APPLICABLE</div>
                <div style={{ fontWeight: 800, color: ST.green }}>${applicable.toFixed(2)}</div>
              </div>
            </div>

            {/* Full / Partial toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {["full","partial"].map(t => (
                <button key={t} onClick={() => setRefundType(t)}
                  style={{ flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12,
                    border: `2px solid ${refundType === t ? "#6366f1" : (isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0")}`,
                    background: refundType === t ? "rgba(99,102,241,0.1)" : "transparent",
                    color: refundType === t ? "#6366f1" : T.textSub }}>
                  {t === "full" ? `Full ($${applicable.toFixed(2)})` : "Partial"}
                </button>
              ))}
            </div>

            {refundType === "partial" && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginBottom: 5, textTransform: "uppercase" }}>Refund Amount</div>
                <input type="number" min="0.01" max={applicable} step="0.01"
                  value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder={`Max $${applicable.toFixed(2)}`} style={inp}/>
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginBottom: 5, textTransform: "uppercase" }}>Reason *</div>
              <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Brief description of why the refund is being issued…"
                style={{ ...inp, resize: "vertical" }}/>
            </div>

            {result && !result.ok && (
              <div style={{ fontSize: 12, color: ST.red, background: "rgba(220,38,38,0.07)",
                border: "1px solid rgba(220,38,38,0.2)", borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
                {result.msg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={submit} disabled={working}
                style={{ flex: 2, padding: "9px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "#6366f1", color: "#fff", fontWeight: 700, fontSize: 13, opacity: working ? 0.6 : 1 }}>
                {working ? "Processing…" : "Confirm Refund"}
              </button>
              <button onClick={onClose} style={{ flex: 1, padding: "9px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0"}`,
                background: "transparent", color: T.textSub, fontWeight: 700, fontSize: 12 }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InvoiceTable({ rows, T, emptyLabel, studentId, onRefund }) {
  const { isDark } = useTheme();
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const timeFmt = config?.time_format || "12h";
  const [refundRow, setRefundRow] = useState(null);
  const invoiceSort = useSort(rows, "transaction_date", "desc");

  const SOURCE_LABEL = { oto_booking: "1-on-1", charge: "Charge", manual: "Manual", lane_booking: "Lane" };

  if (!rows.length) return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: T.textMuted, fontSize: 14 }}>
      <FaHistory size={28} style={{ marginBottom: 10, opacity: 0.3 }} />
      <div>{emptyLabel || "No records found."}</div>
    </div>
  );

  const sorted = invoiceSort.sorted;

  const thSt = {
    padding: "10px 12px", textAlign: "left", fontWeight: 800,
    fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase",
    color: T.textPrimary, borderBottom: `1.5px solid ${T.divider}`,
    whiteSpace: "nowrap",
  };
  const tdSt = (bold) => ({ padding: "9px 12px", borderBottom: `1px solid ${T.divider}`, color: bold ? T.textPrimary : T.textSub, fontWeight: bold ? 600 : 400, whiteSpace: "nowrap" });

  return (
    <>
      {refundRow && (
        <RefundModal row={refundRow} studentId={studentId} T={T} isDark={isDark}
          onClose={() => setRefundRow(null)}
          onDone={() => { setRefundRow(null); if (onRefund) onRefund(); }}/>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: T.glass }}>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("billing_ref")}>
                Billing Ref #{invoiceSort.sortArrow("billing_ref")}
              </th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("transaction_date")}>
                Transaction Date{invoiceSort.sortArrow("transaction_date")}
              </th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("transaction_time")}>
                Time{invoiceSort.sortArrow("transaction_time")}
              </th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("source_type")}>
                Type{invoiceSort.sortArrow("source_type")}
              </th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("description")}>
                Description{invoiceSort.sortArrow("description")}
              </th>
              <th style={{ ...thSt, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("amount_due")}>
                Amount{invoiceSort.sortArrow("amount_due")}
              </th>
              <th style={thSt}>Refund</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("status")}>
                Status{invoiceSort.sortArrow("status")}
              </th>
              <th style={thSt}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const amtDue    = parseFloat(r.amount_due || 0);
              const refunded  = parseFloat(r.refunded_amount || 0);
              const available = Math.max(0, amtDue - refunded);
              const canRefund = [...REFUNDABLE_STATUSES, "partial_refund"].includes(r.status) && available > 0;
              return (
                <tr key={`${r.source_type}-${r.source_id}-${i}`}
                  style={{ background: i % 2 === 0 ? "transparent" : T.glass }}>
                  <td style={{ ...tdSt(true), fontFamily: "monospace", fontSize: 12 }}>{r.billing_ref || "—"}</td>
                  <td style={tdSt(false)}>{fmtDate(r.transaction_date, dateFmt)}</td>
                  <td style={tdSt(false)}>{r.transaction_time ? fmtTime(r.transaction_time, timeFmt) : "—"}</td>
                  <td style={tdSt(false)}>
                    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
                      background: T.glass, color: T.textSub }}>
                      {SOURCE_LABEL[r.source_type] || r.source_type}
                    </span>
                  </td>
                  <td style={{ ...tdSt(false), whiteSpace: "normal", wordBreak: "break-word", minWidth: 220, maxWidth: 380 }}>
                    {r.description || "—"}
                  </td>
                  <td style={{ ...tdSt(true), textAlign: "right" }}>
                    ${amtDue.toFixed(2)}
                  </td>
                  <td style={{ ...tdSt(false), minWidth: 100 }}>
                    {refunded > 0 ? (
                      <div style={{ fontSize: 11, lineHeight: 1.7 }}>
                        <div style={{ color: ST.red }}>↩ ${refunded.toFixed(2)}</div>
                        <div style={{ color: available > 0 ? ST.green : T.textMuted }}>
                          Avail: ${available.toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ ...tdSt(false), fontSize: 12, color: T.text }}>
                    {r.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </td>
                  <td style={tdSt(false)}>
                    {canRefund && (
                      <button onClick={() => setRefundRow(r)}
                        style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #6366f144",
                          background: "rgba(99,102,241,0.08)", color: "#6366f1",
                          fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        ↩ Refund
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function useInvoices(studentId, refreshKey) {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/student-billing/${studentId}/invoices`)
      .then(r => r.json())
      .then(d => setInvoices(d.invoices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);
  useEffect(() => { reload(); }, [reload, refreshKey]);
  return { invoices, loading, reload };
}

function TransactionsSubTab({ studentId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [refreshKey, setRefreshKey] = useState(0);
  const { invoices, loading } = useInvoices(studentId, refreshKey);
  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>;
  return <InvoiceTable rows={invoices} T={T} studentId={studentId}
    onRefund={() => setRefreshKey(k => k + 1)}
    emptyLabel="No transactions recorded for this student." />;
}

function PaidFeesSubTab({ studentId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [refreshKey, setRefreshKey] = useState(0);
  const { invoices, loading } = useInvoices(studentId, refreshKey);
  const PAID = ["confirmed", "completed", "checked_in", "paid", "refunded", "partial_refund"];
  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>;
  return <InvoiceTable rows={invoices.filter(i => PAID.includes(i.status))} T={T} studentId={studentId}
    onRefund={() => setRefreshKey(k => k + 1)}
    emptyLabel="No paid transactions found." />;
}

function UnpaidFeesSubTab({ studentId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const { invoices, loading } = useInvoices(studentId);
  const [paying,   setPaying]   = useState({});   // { chargeId: true/false }
  const [msgs,     setMsgs]     = useState({});    // { chargeId: { ok, text } }
  const [showManual, setShowManual] = useState({}); // { chargeId: true }
  const [manualRef, setManualRef]   = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const UNPAID = ["pending_payment", "scheduled", "pending"];
  const rows   = invoices.filter(i => UNPAID.includes(i.status));
  const unpaidSort = useSort(rows, null);

  const collectStripe = async (r) => {
    const id = r.source_id;
    setPaying(p => ({ ...p, [id]: true }));
    setMsgs(m => ({ ...m, [id]: null }));
    try {
      const res = await fetch(`${BASE}/student-billing/${studentId}/charges/${id}/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "stripe", collected_by: "admin" }),
      });
      const d = await res.json();
      setMsgs(m => ({ ...m, [id]: { ok: d.success, text: d.message } }));
      if (d.success) setRefreshKey(k => k + 1);
    } catch {
      setMsgs(m => ({ ...m, [id]: { ok: false, text: "Network error" } }));
    }
    setPaying(p => ({ ...p, [id]: false }));
  };

  const collectManual = async (r) => {
    const id  = r.source_id;
    const ref = (manualRef[id] || "").trim() || "Cash / Manual";
    setPaying(p => ({ ...p, [id]: true }));
    try {
      const res = await fetch(`${BASE}/student-billing/${studentId}/charges/${id}/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "manual", manual_ref: ref, collected_by: "admin" }),
      });
      const d = await res.json();
      setMsgs(m => ({ ...m, [id]: { ok: d.success, text: d.message } }));
      if (d.success) { setShowManual(s => ({ ...s, [id]: false })); setRefreshKey(k => k + 1); }
    } catch {
      setMsgs(m => ({ ...m, [id]: { ok: false, text: "Network error" } }));
    }
    setPaying(p => ({ ...p, [id]: false }));
  };

  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>;

  if (!rows.length) return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: T.textMuted, fontSize: 14 }}>
      <FaCheckCircle size={28} style={{ marginBottom: 10, opacity: 0.3 }} />
      <div>No unpaid transactions — all clear!</div>
    </div>
  );

  const thSt = { padding: "10px 12px", textAlign: "left", fontWeight: 800, fontSize: 10,
    letterSpacing: "0.07em", textTransform: "uppercase", color: T.textPrimary,
    borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" };
  const tdSt = (bold) => ({ padding: "9px 12px", borderBottom: `1px solid ${T.divider}`,
    color: bold ? T.textPrimary : T.textSub, fontWeight: bold ? 600 : 400, whiteSpace: "nowrap" });

  return (
    <div style={{ overflowX: "auto" }} key={refreshKey}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.glass }}>
            <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => unpaidSort.toggleSort("billing_ref")}>Billing Ref #{unpaidSort.sortArrow("billing_ref")}</th>
            <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => unpaidSort.toggleSort("transaction_date")}>Date{unpaidSort.sortArrow("transaction_date")}</th>
            <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => unpaidSort.toggleSort("description")}>Description{unpaidSort.sortArrow("description")}</th>
            <th style={{ ...thSt, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => unpaidSort.toggleSort("amount_due")}>Amount{unpaidSort.sortArrow("amount_due")}</th>
            <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => unpaidSort.toggleSort("status")}>Status{unpaidSort.sortArrow("status")}</th>
            <th style={thSt}>Collect Payment</th>
          </tr>
        </thead>
        <tbody>
          {unpaidSort.sorted.map((r, i) => {
            const id  = r.source_id;
            const msg = msgs[id];
            return (
              <React.Fragment key={`${r.source_type}-${id}-${i}`}>
                <tr style={{ background: i % 2 === 0 ? "transparent" : T.glass }}>
                  <td style={{ ...tdSt(true), fontFamily: "monospace", fontSize: 12 }}>{r.billing_ref || "—"}</td>
                  <td style={tdSt(false)}>{fmtDate(r.transaction_date, dateFmt)}</td>
                  <td style={{ ...tdSt(false), whiteSpace: "normal", wordBreak: "break-word", minWidth: 220, maxWidth: 380 }}>
                    {r.description || "—"}
                    {r.source === "auto" && (
                      <span title={r.schedule_id ? `Recurring schedule #${r.schedule_id}` : "Automated recurring charge"}
                        style={{ marginLeft: 6, padding: "1px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                          color: "#6366f1", background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.25)" }}>
                        ↻ Auto
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdSt(true), textAlign: "right" }}>${parseFloat(r.amount_due || 0).toFixed(2)}</td>
                  <td style={tdSt(false)}>
                    <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                      color: ST.orange, background: `${ST.orange}18`, border: `1px solid ${ST.orange}44` }}>
                      {r.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td style={{ ...tdSt(false), display: "flex", gap: 6, alignItems: "center" }}>
                    {r.source_type === "charge" && (
                      <>
                        <button onClick={() => collectStripe(r)} disabled={paying[id]}
                          style={{ padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                            background: ST.green, color: "#fff", fontSize: 11, fontWeight: 700 }}>
                          {paying[id] ? "…" : "Stripe Pay"}
                        </button>
                        <button onClick={() => setShowManual(s => ({ ...s, [id]: !s[id] }))}
                          style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.divider}`,
                            cursor: "pointer", background: T.glass, color: T.textSub, fontSize: 11 }}>
                          Manual
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {showManual[id] && (
                  <tr>
                    <td colSpan={6} style={{ padding: "8px 16px", background: T.glass }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input value={manualRef[id] || ""} placeholder="Reference (Cash / Cheque / Bank Transfer)"
                          onChange={e => setManualRef(m => ({ ...m, [id]: e.target.value }))}
                          style={{ flex: 1, padding: "6px 10px", borderRadius: 8,
                            border: `1px solid ${T.divider}`, background: T.inputBg, color: T.textPrimary, fontSize: 12 }} />
                        <button onClick={() => collectManual(r)} disabled={paying[id]}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                            background: ST.yellow, color: "#1a2840", fontSize: 12, fontWeight: 700 }}>
                          {paying[id] ? "Saving…" : "Mark as Paid"}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {msg && (
                  <tr>
                    <td colSpan={6} style={{ padding: "6px 16px", fontSize: 12,
                      color: msg.ok ? ST.green : ST.red, background: msg.ok ? `${ST.green}10` : `${ST.red}10` }}>
                      {msg.ok ? "✓" : "✗"} {msg.text}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const CARD_BRAND_LABELS = { visa:"VISA", mastercard:"MC", amex:"AMEX", discover:"DISC", jcb:"JCB", unionpay:"UP" };

function PaymentProfileSubTab({ studentId }) {
  const { isDark } = useTheme(); const T = getT(isDark);

  const [cardInfo,    setCardInfo]    = useState(null);
  const [parentId,    setParentId]    = useState(null);
  const [parentName,  setParentName]  = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [processing,  setProcessing]  = useState(false);
  const [removing,    setRemoving]    = useState(false);
  const [msg,         setMsg]         = useState(null);

  const stripeRef  = useRef(null);
  const cardElRef  = useRef(null);
  const mountedRef = useRef(false);

  const loadCard = () => {
    setLoading(true);
    fetch(`${BASE}/oto/payment-method-by-student/${studentId}`)
      .then(r => r.json())
      .then(d => {
        setCardInfo(d.card || null);
        setParentId(d.parent_id || null);
        setParentName(d.parent_name || "");
        setParentEmail(d.parent_email || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCard(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount Stripe card element when form opens
  useEffect(() => {
    if (!showForm || !STRIPE_PK || mountedRef.current) return;
    let cancelled = false;
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: {
          base: {
            fontSize: "15px",
            color: isDark ? "#e2e8f0" : "#1a2840",
            "::placeholder": { color: isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)" },
          },
        },
      });
      const el = document.getElementById("admin-card-element");
      if (el) { card.mount("#admin-card-element"); cardElRef.current = card; mountedRef.current = true; }
    });
    return () => { cancelled = true; };
  }, [showForm, isDark]);

  const openForm = () => {
    mountedRef.current = false; cardElRef.current = null;
    setMsg(null); setShowForm(true);
  };

  const closeForm = () => {
    if (cardElRef.current) { try { cardElRef.current.unmount(); } catch {} }
    cardElRef.current = null; mountedRef.current = false;
    setShowForm(false); setMsg(null);
  };

  const handleSaveCard = async () => {
    if (!stripeRef.current || !cardElRef.current) {
      setMsg({ ok: false, text: "Card form not ready — please wait a moment." }); return;
    }
    if (!parentId) {
      setMsg({ ok: false, text: "No parent account linked to this student." }); return;
    }
    setProcessing(true); setMsg(null);
    try {
      const siRes  = await fetch(`${BASE}/oto/parent-setup-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: parentId, email: parentEmail, name: parentName }),
      });
      const siData = await siRes.json();
      if (!siData.success) throw new Error(siData.message || "Could not initialise payment setup");

      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(
        siData.client_secret,
        { payment_method: { card: cardElRef.current, billing_details: { name: parentName, email: parentEmail } } }
      );
      if (error) throw new Error(error.message);

      const saveRes  = await fetch(`${BASE}/oto/parent-save-payment-method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: parentId, payment_method_id: setupIntent.payment_method }),
      });
      const saveData = await saveRes.json();
      if (!saveData.success) throw new Error(saveData.message || "Could not save card");

      setCardInfo(saveData.card);
      setMsg({ ok: true, text: "Card saved successfully." });
      closeForm();
    } catch (e) {
      setMsg({ ok: false, text: e.message || "Something went wrong." });
    }
    setProcessing(false);
  };

  const handleRemove = async () => {
    if (!parentId) return;
    if (!window.confirm("Remove this card? The parent will need to re-add their card before billing.")) return;
    setRemoving(true);
    try {
      const r = await fetch(`${BASE}/oto/parent-payment-method/${parentId}`, { method: "DELETE" });
      const d = await r.json();
      if (d.success || r.ok) { setCardInfo(null); setMsg({ ok: true, text: "Card removed." }); }
      else setMsg({ ok: false, text: d.message || "Failed to remove card." });
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setRemoving(false);
  };

  if (!STRIPE_PK) {
    return (
      <div style={{ maxWidth: 520 }}>
        <div style={{ padding: "16px 20px", borderRadius: 12,
          background: T.orangeBg, border: `1px solid ${T.orange}44`, color: T.orange, fontSize: 13 }}>
          <strong>Stripe not configured.</strong> Add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your environment and rebuild.
        </div>
      </div>
    );
  }

  // Use theme glass tokens — correct in both light and dark
  const panelSt = {
    background:    T.glass,
    border:        T.glassBdr,
    boxShadow:     T.glassSh,
    borderRadius:  14,
    padding:       "18px 20px",
    backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>
        Card details are stored securely with Stripe — never on our servers.
      </div>

      {msg && !showForm && (
        <div style={{ ...toastSt(msg.ok), marginBottom: 14 }}>
          {msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}
        </div>
      )}

      {loading ? (
        <div style={{ color: T.textMuted, fontSize: 13 }}>Checking payment method…</div>

      ) : !parentId ? (
        <div style={{ ...panelSt, fontSize: 13, color: T.textMuted, display: "flex", alignItems: "center", gap: 10 }}>
          <FaCreditCard size={18} style={{ opacity: 0.4 }} />
          No parent account is linked to this student. Link a parent first via the student profile.
        </div>

      ) : cardInfo ? (
        <div style={{ ...panelSt, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Brand chip */}
          <div style={{
            width: 52, height: 34, borderRadius: 6,
            background: ST.blue,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: 0.5, flexShrink: 0,
          }}>
            {CARD_BRAND_LABELS[cardInfo.brand?.toLowerCase()] || cardInfo.brand?.toUpperCase() || "CARD"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: T.textPrimary }}>
              •••• •••• •••• {cardInfo.last4}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              Expires {String(cardInfo.exp_month).padStart(2,"0")}/{String(cardInfo.exp_year).slice(-2)}
            </div>
          </div>
          <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: T.greenBg, border: `1px solid ${T.green}55`, color: T.green,
          }}>Active</span>
          <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 2 }}>
            <GhostButton onClick={openForm} style={{ padding: "7px 16px", fontSize: 12 }}>Replace Card</GhostButton>
            <DangerButton onClick={handleRemove} disabled={removing}>
              <FaTimesCircle size={11} /> {removing ? "Removing…" : "Remove Card"}
            </DangerButton>
          </div>
        </div>

      ) : (
        !showForm && (
          <div style={{ ...panelSt, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>No card on file</div>
            <PrimaryButton onClick={openForm}>+ Add Card</PrimaryButton>
          </div>
        )
      )}

      {/* ── Stripe Elements card form ── */}
      {showForm && (
        <div style={{ ...panelSt, marginTop: cardInfo ? 14 : 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 14 }}>
            {cardInfo ? "Replace Card" : "Add Card"}
          </div>

          <label style={labelSt(T)}>Card Details</label>

          {/* Stripe injects card input here — no card data in our DOM */}
          <div id="admin-card-element" style={{
            padding: "12px 14px", borderRadius: 9,
            background: T.inputBg,
            border: `1.5px solid ${T.inputBdr}`,
            minHeight: 44,
          }} />

          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8, marginBottom: 16 }}>
            🔒 Encrypted and handled by Stripe. The card number is never sent to Sixers servers.
          </div>

          {msg && (
            <div style={{ ...toastSt(msg.ok), marginBottom: 14 }}>
              {msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryButton onClick={handleSaveCard} disabled={processing} style={{ flex: 1 }}>{processing ? "Saving…" : "Save Card"}</PrimaryButton>
            <GhostButton onClick={closeForm} disabled={processing}>
              Cancel
            </GhostButton>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Billing Cycle helpers ────────────────────────────────────────────────────
const CYCLE_LABELS = { monthly: "Monthly", quarterly: "Quarterly", semi_annual: "Semi-Annual", annual: "Annual" };
const CYCLE_FEE_KEY = { monthly: "fee_monthly", quarterly: "fee_quarterly", semi_annual: "fee_semi_annual", annual: "fee_annual" };
const CYCLE_MONTHS  = { monthly: 1, quarterly: 3, semi_annual: 6, annual: 12 };

function KV({ label, value, accent }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: accent || T.textPrimary, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function BillingCostRow({ checked, onToggle, label, amount, onAmount, amountLabel, override, onOverride, notes, onNotes, notesLabel, alwaysOn }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const inp = { border: `1.5px solid ${T.inputBdr}`, borderRadius: 8, padding: "7px 10px", background: T.inputBg, color: T.inputText, fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" };
  return (
    <div style={{ borderRadius: 10, border: `1.5px solid ${checked ? ST.blue + "55" : T.divider}`, background: checked ? (isDark ? "rgba(3,78,123,0.10)" : "rgba(3,78,123,0.04)") : "transparent", padding: "12px 14px", transition: "all 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {!alwaysOn && (
          <input type="checkbox" checked={checked} onChange={e => onToggle(e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer", accentColor: ST.blue }} />
        )}
        {alwaysOn && <div style={{ width: 16, height: 16, borderRadius: 4, background: ST.blue, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#fff", fontSize: 9 }}>✓</span></div>}
        <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, flex: 1 }}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>$</span>
          <input type="number" min="0" step="0.01" value={amount} onChange={e => onAmount(e.target.value)}
            disabled={!checked && !alwaysOn}
            style={{ ...inp, width: 90, opacity: (!checked && !alwaysOn) ? 0.4 : 1 }} />
        </div>
      </div>
      {checked && override !== undefined && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={override} onChange={e => onOverride(e.target.checked)}
            style={{ accentColor: ST.blue }} />
          <span style={{ fontSize: 12, color: T.textMuted }}>Override default amount</span>
        </div>
      )}
      {checked && onNotes && (
        <input type="text" placeholder={notesLabel || "Notes (optional)"} value={notes} onChange={e => onNotes(e.target.value)}
          style={{ ...inp, marginTop: 8, fontSize: 12 }} />
      )}
    </div>
  );
}

function RecurringSubTab({ studentId, currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";

  // ── Data ──
  const [data,      setData]      = useState(null);   // { billing, proration, charges, schedules }
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState(null);

  // ── One-time charge form ──
  const [includeAdmin,      setIncludeAdmin]      = useState(false);
  const [adminCost,         setAdminCost]         = useState("0.00");
  const [adminOverridden,   setAdminOverridden]   = useState(false);
  const [adminNotes,        setAdminNotes]        = useState("");
  const [includeJersey,     setIncludeJersey]     = useState(false);
  const [jerseyCost,        setJerseyCost]        = useState("");
  const [includeTournament, setIncludeTournament] = useState(false);
  const [tournamentCost,    setTournamentCost]    = useState("");
  const [chargeNotes,       setChargeNotes]       = useState("");
  const [paying,            setPaying]            = useState(false);

  // ── Stripe (for manual card entry if no saved card) ──
  const stripeRef  = useRef(null);
  const cardElRef  = useRef(null);
  const mountedRef = useRef(false);
  const [needCard,  setNeedCard]  = useState(false);
  const [cardReady, setCardReady] = useState(false);

  // ── Recurring schedule form ──
  const [showSchedForm, setShowSchedForm] = useState(false);
  const [schedForm, setSchedForm] = useState({ frequency: "monthly", amount: "", next_run_date: "", program_from: "", program_to: "" });
  const [schedMsg,  setSchedMsg]  = useState(null);
  const [editSchedId,   setEditSchedId]   = useState(null);
  const [editSchedForm, setEditSchedForm] = useState({});
  const [chargingSchedId, setChargingSchedId] = useState(null);
  const [showAdhocForm, setShowAdhocForm] = useState(false);
  const [adhocDesc,     setAdhocDesc]     = useState("");
  const [adhocAmt,      setAdhocAmt]      = useState("");
  const [adhocPaying,   setAdhocPaying]   = useState(false);
  const [collectingId,  setCollectingId]  = useState(null);   // charge_id being collected
  const [collectMsg,    setCollectMsg]    = useState({});      // { [charge_id]: {ok,text} }
  const [showManualRow, setShowManualRow] = useState({});     // { [charge_id]: bool }
  const [manualRefRow,  setManualRefRow]  = useState({});     // { [charge_id]: string }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/student-billing/${studentId}/billing-cycle`);
      const d = await r.json();
      setData(d);
      // Seed admin cost from program default
      const progAdminCost = d?.billing?.prog_admin_cost || 0;
      const adminVal = parseFloat(progAdminCost || 0);
      setAdminCost(adminVal.toFixed(2));
      if (adminVal > 0) setIncludeAdmin(true);
      // Seed schedule form with net discounted fee
      const billing = d?.billing;
      if (billing) {
        const netKey = { monthly: "net_fee_monthly", quarterly: "net_fee_quarterly", semi_annual: "net_fee_semi_annual", annual: "net_fee_annual" }[billing.billing_cycle] || "net_fee_monthly";
        const netAmt = parseFloat(billing[netKey] || billing[CYCLE_FEE_KEY[billing.billing_cycle]] || 0);
        setSchedForm(f => ({ ...f, frequency: billing.billing_cycle || "monthly", amount: netAmt.toFixed(2), next_run_date: d?.proration?.next_period_start || "" }));
      }
    } catch { setData(null); } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const schedTableSort = useSort(data?.schedules || [], "next_run_date");

  // Mount Stripe card element when needed
  useEffect(() => {
    if (!needCard || !STRIPE_PK || mountedRef.current) return;
    let cancelled = false;
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: { base: { fontSize: "14px", color: isDark ? "#e2e8f0" : "#1a2840",
          "::placeholder": { color: isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)" } } },
      });
      const el = document.getElementById("billing-cycle-card-el");
      if (el) { card.mount("#billing-cycle-card-el"); cardElRef.current = card; mountedRef.current = true; setCardReady(true); }
    });
    return () => { cancelled = true; };
  }, [needCard, isDark]);

  // ── Computed totals ──
  const proration     = data?.proration;
  const billing       = data?.billing;
  const prorateAmt    = parseFloat(proration?.prorate_amount || 0);
  const adminAmt      = includeAdmin ? parseFloat(adminCost || 0) : 0;
  const jerseyAmt     = includeJersey     ? parseFloat(jerseyCost    || 0) : 0;
  const tournamentAmt = includeTournament ? parseFloat(tournamentCost || 0) : 0;
  const totalOneTime  = prorateAmt + adminAmt + jerseyAmt + tournamentAmt;

  // Split charges: initial = has any structured cost; adhoc = pure description+amount.
  // Charges the Billing Hub generated automatically (source==='auto') are neither —
  // they're driven by the Recurring Schedule card above, not a one-off decision made
  // here, so they're excluded from both buckets on this tab. They still show up (with
  // full detail) on the student's All Transactions / Paid Fees tabs, same as any charge.
  const isInitialCharge = c =>
    parseFloat(c.prorate_amount || 0) > 0 ||
    parseFloat(c.admin_cost     || 0) > 0 ||
    parseFloat(c.jersey_cost    || 0) > 0 ||
    parseFloat(c.tournament_cost|| 0) > 0;

  const PROCESSED_STATUSES = ["paid", "refunded", "partial_refund"];
  const nonScheduledCharges = (data?.charges || []).filter(c => c.source !== "auto");
  const initialCharges = nonScheduledCharges.filter(c =>  isInitialCharge(c));
  const adhocCharges   = nonScheduledCharges.filter(c => !isInitialCharge(c));
  const hasPaidCharge  = initialCharges.some(c => PROCESSED_STATUSES.includes(c.status));

  // ── Pay handler ──
  const handlePay = async () => {
    if (totalOneTime <= 0) { setMsg({ ok: false, text: "Total must be greater than $0.00." }); return; }
    setPaying(true); setMsg(null);
    try {
      const payload = {
        program_id: billing?.program_id || null,
        billing_cycle: billing?.billing_cycle || "monthly",
        prorate_days: proration?.prorate_days || 0,
        prorate_days_total: proration?.prorate_days_total || 0,
        prorate_amount: prorateAmt,
        admin_cost: includeAdmin ? adminAmt : 0,
        admin_cost_overridden: adminOverridden,
        admin_cost_notes: adminNotes || null,
        jersey_cost: jerseyAmt,
        include_jersey: includeJersey,
        tournament_cost: tournamentAmt,
        include_tournament: includeTournament,
        total_amount: totalOneTime,
        notes: chargeNotes || null,
        created_by: currentUserId || "admin",
      };

      const res  = await fetch(`${BASE}/student-billing/${studentId}/billing-cycle/charge`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const data2 = await res.json();
      if (!data2.success) throw new Error(data2.message || "Failed to initiate charge.");

      if (data2.charge_status === "paid") {
        setMsg({ ok: true, text: `Payment successful! Ref: ${data2.billing_ref}` });
        load();
      } else if (data2.client_secret && stripeRef.current && cardElRef.current) {
        // Confirm with Stripe Elements card
        const { paymentIntent, error } = await stripeRef.current.confirmCardPayment(
          data2.client_secret, { payment_method: { card: cardElRef.current } }
        );
        if (error) throw new Error(error.message);
        if (paymentIntent.status === "succeeded") {
          await fetch(`${BASE}/student-billing/${studentId}/billing-cycle/confirm-charge/${data2.charge_id}`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
          });
          setMsg({ ok: true, text: `Payment successful! Ref: ${data2.billing_ref}` });
          load();
        }
      } else if (data2.client_secret && !cardElRef.current) {
        // Need card entry
        setNeedCard(true);
        setMsg({ ok: false, text: "No saved card found. Please enter card details below and click Pay again." });
      } else {
        setMsg({ ok: true, text: `Charge recorded (${data2.billing_ref}). Awaiting payment.` });
        load();
      }
    } catch (e) {
      setMsg({ ok: false, text: e.message || "Payment failed." });
    }
    setPaying(false);
  };

  const handleAddSchedule = async () => {
    if (!schedForm.amount) { setSchedMsg({ ok: false, text: "Amount is required." }); return; }
    try {
      const r = await fetch(`${BASE}/student-billing/${studentId}/schedules`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...schedForm, program_id: billing?.program_id || null, created_by: currentUserId || "admin" }),
      });
      const d = await r.json();
      if (d.success) { setSchedMsg({ ok: true, text: "Schedule created." }); setShowSchedForm(false); load(); }
      else setSchedMsg({ ok: false, text: d.message || "Failed." });
    } catch { setSchedMsg({ ok: false, text: "Network error." }); }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await fetch(`${BASE}/student-billing/schedules/${id}`, { method: "DELETE" });
    load();
  };

  const chargeScheduleNow = async (id) => {
    if (!window.confirm("Charge this student's saved card for the current billing cycle right now?")) return;
    setChargingSchedId(id); setSchedMsg(null);
    try {
      const res = await fetch(`${BASE}/billing-schedule/schedules/student/${id}/charge-now`, { method: "POST" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Charge failed.");
      setSchedMsg({ ok: d.success > 0, text: d.success > 0 ? "Charge succeeded." : d.skipped > 0 ? "Nothing to charge — this cycle is already paid or not yet due." : "Charge failed — see the student's transaction history for the reason." });
      load();
    } catch (e) {
      setSchedMsg({ ok: false, text: e.message || "Charge failed." });
    } finally {
      setChargingSchedId(null);
    }
  };

  const startEditSched = (s) => {
    setEditSchedId(s.schedule_id);
    setEditSchedForm({ frequency: s.frequency, amount: String(s.amount || ""), next_run_date: s.next_run_date || "", program_from: s.program_from || "", program_to: s.program_to || "" });
  };

  const handleAdhocPay = async () => {
    const amt = parseFloat(adhocAmt);
    if (!adhocDesc.trim()) { setMsg({ ok: false, text: "Description is required." }); return; }
    if (!amt || amt <= 0)  { setMsg({ ok: false, text: "Amount must be greater than $0." }); return; }
    setAdhocPaying(true); setMsg(null);
    try {
      const res  = await fetch(`${BASE}/student-billing/${studentId}/billing-cycle/charge`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          program_id: billing?.program_id || null,
          billing_cycle: billing?.billing_cycle || "monthly",
          prorate_amount: 0, prorate_days: 0, prorate_days_total: 0,
          admin_cost: 0, jersey_cost: 0, tournament_cost: 0,
          total_amount: amt, notes: adhocDesc.trim(),
          created_by: currentUserId || "admin",
        }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || "Failed.");
      if (d.charge_status === "paid") {
        setMsg({ ok: true, text: `Collected $${amt.toFixed(2)} — Ref: ${d.billing_ref}` });
        setAdhocDesc(""); setAdhocAmt(""); setShowAdhocForm(false); load();
      } else if (d.client_secret && stripeRef.current && cardElRef.current) {
        const { paymentIntent, error } = await stripeRef.current.confirmCardPayment(
          d.client_secret, { payment_method: { card: cardElRef.current } }
        );
        if (error) throw new Error(error.message);
        if (paymentIntent.status === "succeeded") {
          await fetch(`${BASE}/student-billing/${studentId}/billing-cycle/confirm-charge/${d.charge_id}`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
          });
          setMsg({ ok: true, text: `Collected $${amt.toFixed(2)} — Ref: ${d.billing_ref}` });
          setAdhocDesc(""); setAdhocAmt(""); setShowAdhocForm(false); load();
        }
      } else if (d.client_secret && !cardElRef.current) {
        setNeedCard(true);
        setMsg({ ok: false, text: "No saved card. Enter card details and try again." });
      } else {
        setMsg({ ok: true, text: `Charge recorded (${d.billing_ref}). Awaiting payment.` });
        setAdhocDesc(""); setAdhocAmt(""); setShowAdhocForm(false); load();
      }
    } catch (e) { setMsg({ ok: false, text: e.message || "Payment failed." }); }
    setAdhocPaying(false);
  };

  const handleCollectCharge = async (chargeId, mode) => {
    setCollectingId(chargeId);
    setCollectMsg(m => ({ ...m, [chargeId]: null }));
    try {
      const ref = (manualRefRow[chargeId] || "").trim() || "Cash / Manual";
      const res = await fetch(`${BASE}/student-billing/${studentId}/charges/${chargeId}/collect`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, manual_ref: ref, collected_by: currentUserId || "admin" }),
      });
      const d = await res.json();
      setCollectMsg(m => ({ ...m, [chargeId]: { ok: d.success, text: d.message } }));
      if (d.success) { setShowManualRow(s => ({ ...s, [chargeId]: false })); load(); }
    } catch {
      setCollectMsg(m => ({ ...m, [chargeId]: { ok: false, text: "Network error." } }));
    }
    setCollectingId(null);
  };

  const handleUpdateSchedule = async () => {
    try {
      const r = await fetch(`${BASE}/student-billing/schedules/${editSchedId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editSchedForm, program_id: billing?.program_id || null }),
      });
      const d = await r.json();
      if (d.success) { setEditSchedId(null); setSchedMsg({ ok: true, text: "Schedule updated." }); load(); }
      else setSchedMsg({ ok: false, text: d.message || "Update failed." });
    } catch { setSchedMsg({ ok: false, text: "Network error." }); }
  };

  // ── Styles ──
  const card = (accent) => ({
    background: T.glass, border: accent ? `1.5px solid ${accent}44` : T.glassBdr,
    borderRadius: 14, padding: "18px 20px",
    backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  });
  const sectionHead = (label, sub) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const divider = <div style={{ height: 1, background: T.divider, margin: "20px 0" }} />;
  const amtRow  = (label, amt, muted) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0" }}>
      <span style={{ fontSize: 13, color: muted ? T.textMuted : T.textSub }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: muted ? 400 : 600, color: muted ? T.textMuted : T.textPrimary }}>${parseFloat(amt || 0).toFixed(2)}</span>
    </div>
  );

  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>;

  if (!billing?.program_id) {
    return (
      <div style={{ ...card(), textAlign: "center", padding: "36px 24px" }}>
        <FaRegClock size={32} style={{ opacity: 0.25, marginBottom: 12 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>No coaching program assigned</div>
        <div style={{ fontSize: 12, color: T.textMuted }}>Assign a coaching program in the Coaching Program tab first.</div>
      </div>
    );
  }

  const cycleLabel = CYCLE_LABELS[billing.billing_cycle] || billing.billing_cycle;
  // Use net (discounted) fee for display
  const netFeeKey  = { monthly:"net_fee_monthly", quarterly:"net_fee_quarterly", semi_annual:"net_fee_semi_annual", annual:"net_fee_annual" }[billing.billing_cycle] || "net_fee_monthly";
  const cycleFee   = parseFloat(billing[netFeeKey] || billing[CYCLE_FEE_KEY[billing.billing_cycle]] || 0);
  const baseFee    = parseFloat(billing[CYCLE_FEE_KEY[billing.billing_cycle]] || 0);
  const hasSaving  = baseFee > cycleFee;

  // Compact checkbox row style
  const chkRow = (checked) => ({
    display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
    borderRadius: 9, border: `1.5px solid ${checked ? ST.blue + "66" : T.divider}`,
    background: checked ? (isDark ? "rgba(3,78,123,0.12)" : "rgba(3,78,123,0.05)") : "transparent",
    cursor: "pointer", transition: "all 0.12s",
  });
  const numInp = { width: 80, border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "5px 8px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", textAlign: "right" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {msg && <div style={{ ...toastSt(msg.ok) }}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      {/* ── Program + Proration summary bar ── */}
      <div style={{ ...card(ST.blue), padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px 28px", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Program</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary, marginTop: 2 }}>{billing.program_name}</div>
          </div>
          <div style={{ height: 32, width: 1, background: T.divider }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Billing Cycle</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: ST.blue, marginTop: 2 }}>{cycleLabel}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Cycle Fee</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 2 }}>
              {hasSaving && <span style={{ fontSize: 11, color: T.textMuted, textDecoration: "line-through" }}>${baseFee.toFixed(2)}</span>}
              <span style={{ fontSize: 14, fontWeight: 800, color: ST.blue }}>${cycleFee.toFixed(2)}</span>
            </div>
          </div>
          {billing.level_name && (
            <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: T.glass, border: T.glassBdr, color: T.textSub }}>{billing.level_name}</span>
          )}
          <div style={{ marginLeft: "auto" }}>
            {proration?.aligned
              ? <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: T.greenBg, border: `1px solid ${T.green}44`, color: T.green }}>Aligned — no proration</span>
              : <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: T.orangeBg, border: `1px solid ${T.orange}44`, color: T.orange }}>
                  Prorated: {proration?.prorate_days}/{proration?.prorate_days_total} days = ${proration?.prorate_amount?.toFixed(2)}
                </span>
            }
          </div>
        </div>
      </div>

      {/* ── Billing Breakdown — every component that makes up the Cycle Fee
             above, itemized. The summary bar's "Cycle Fee" only ever showed
             the final net number, silently folding in (or, before this fix,
             silently DROPPING) discounts and extra fees with no way to see
             what was actually applied. ── */}
      {(() => {
        const cm = CYCLE_MONTHS[billing.billing_cycle] || 1;
        // This card explains the RECURRING Cycle Fee above it — a one-time
        // fee (e.g. Jersey) has zero effect on that number and is already
        // shown in its own right in the One-Time Charge card below, so
        // including it here too was pure duplication with no informational
        // value (worse, it read as if it were part of the recurring total).
        const recurring = (billing.extra_fees || []).filter(f => f.recurring);
        const baseBeforeExtras = parseFloat(billing.net_fee_base != null ? billing.net_fee_base : baseFee);
        const progDiscAmt = (() => {
          const dt = billing[`disc_${billing.billing_cycle === "semi_annual" ? "semi" : billing.billing_cycle}_type`];
          const dv = parseFloat(billing[`disc_${billing.billing_cycle === "semi_annual" ? "semi" : billing.billing_cycle}_value`] || 0);
          if (!dt || !dv) return 0;
          return dt === "pct" ? baseFee * dv / 100 : dv;
        })();
        const studentDiscAmt = Math.max(0, (baseFee - progDiscAmt) - baseBeforeExtras);
        const hasAnything = progDiscAmt > 0 || studentDiscAmt > 0 || recurring.length > 0;
        if (!hasAnything) return null;
        return (
          <div style={card()}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.textPrimary, marginBottom: 10 }}>Billing Breakdown — {cycleLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: T.textSub }}>Program Fee</span>
                <span style={{ fontWeight: 700, color: T.textPrimary }}>${baseFee.toFixed(2)}</span>
              </div>
              {progDiscAmt > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: T.textSub }}>Program Discount</span>
                  <span style={{ fontWeight: 700, color: "#ef4444" }}>-${progDiscAmt.toFixed(2)}</span>
                </div>
              )}
              {studentDiscAmt > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: T.textSub }}>{billing.discount_reason || "Student Discount"}</span>
                  <span style={{ fontWeight: 700, color: "#ef4444" }}>-${studentDiscAmt.toFixed(2)}</span>
                </div>
              )}
              {recurring.map((f, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: T.textSub }}>{f.label} <span style={{ fontSize: 10, color: T.textMuted }}>(${parseFloat(f.amount||0).toFixed(2)}/mo x {cm} months)</span></span>
                  <span style={{ fontWeight: 700, color: T.textPrimary }}>${(parseFloat(f.amount || 0) * cm).toFixed(2)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, marginTop: 2, borderTop: `1px solid ${T.divider}` }}>
                <span style={{ fontWeight: 800, color: T.textPrimary }}>Recurring Total ({cycleLabel})</span>
                <span style={{ fontWeight: 900, color: ST.blue }}>${cycleFee.toFixed(2)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── One-Time Charge ── */}
      <div style={card(hasPaidCharge ? T.green : null)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>One-Time Charge</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              {hasPaidCharge ? "Initial charge collected — record below." : "Select items to include in the first payment."}
            </div>
          </div>
          {hasPaidCharge && (
          <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: initialCharges.some(c => c.status === "partial_refund") ? "rgba(168,85,247,0.10)" : T.greenBg,
            color: initialCharges.some(c => c.status === "partial_refund") ? "#a855f7" : T.green,
            border: `1px solid ${initialCharges.some(c => c.status === "partial_refund") ? "#a855f7" : T.green}44` }}>
            {initialCharges.some(c => c.status === "refunded") ? "Refunded"
              : initialCharges.some(c => c.status === "partial_refund") ? "Partial Refund"
              : "Paid ✓"}
          </span>
        )}
        </div>

        {hasPaidCharge ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {initialCharges.filter(c => PROCESSED_STATUSES.includes(c.status)).map(c => {
              const isRefunded = c.status === "refunded";
              const isPartial  = c.status === "partial_refund";
              const refundedAmt = parseFloat(c.refunded_amount || 0);
              const rowColor = isRefunded ? "#a855f7" : isPartial ? "#a855f7" : T.green;
              const rowBg    = isRefunded ? "rgba(168,85,247,0.08)" : isPartial ? "rgba(168,85,247,0.08)" : T.greenBg;
              const rowBdr   = isRefunded ? "rgba(168,85,247,0.2)" : isPartial ? "rgba(168,85,247,0.2)" : `${T.green}33`;
              const breakdown = [];
              if (parseFloat(c.prorate_amount || 0) > 0) breakdown.push(`Program (prorated): $${parseFloat(c.prorate_amount).toFixed(2)}`);
              if (parseFloat(c.admin_cost || 0) > 0)     breakdown.push(`Admin fee: $${parseFloat(c.admin_cost).toFixed(2)}`);
              if (parseFloat(c.jersey_cost || 0) > 0)    breakdown.push(`Jersey: $${parseFloat(c.jersey_cost).toFixed(2)}`);
              if (parseFloat(c.tournament_cost || 0) > 0) breakdown.push(`Tournament: $${parseFloat(c.tournament_cost).toFixed(2)}`);
              return (
              <div key={c.charge_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                background: rowBg, borderRadius: 9, padding: "9px 14px", border: `1px solid ${rowBdr}`, gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: rowColor, fontFamily: "monospace" }}>{c.billing_ref}</span>
                    <span style={{ fontSize: 12, color: T.textMuted }}>{fmtDate(c.charge_date, dateFmt)}</span>
                    {(isRefunded || isPartial) && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: rowColor, padding: "1px 7px", borderRadius: 10, background: `${rowColor}18` }}>
                        {isRefunded ? "Refunded" : `Partial Refund — ↩$${refundedAmt.toFixed(2)}`}
                      </span>
                    )}
                  </div>
                  {breakdown.length > 0 && (
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>{breakdown.join(" · ")}</div>
                  )}
                  {c.notes && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, fontStyle: "italic" }}>{c.notes}</div>}
                </div>
                <span style={{ fontSize: 14, fontWeight: 800, color: rowColor, whiteSpace: "nowrap" }}>${parseFloat(c.total_amount).toFixed(2)}</span>
              </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 16 }}>
            {/* Left: checkboxes */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Proration — always on */}
              <div style={chkRow(true)}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: ST.blue, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>
                </div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.textPrimary }}>
                  {proration?.aligned ? `${cycleLabel} Fee` : `Prorated (${proration?.prorate_days}/${proration?.prorate_days_total} days)`}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: ST.blue }}>${prorateAmt.toFixed(2)}</span>
              </div>

              {/* Admin Cost */}
              <div style={chkRow(includeAdmin)} onClick={() => setIncludeAdmin(v => !v)}>
                <input type="checkbox" checked={includeAdmin} onChange={e => { e.stopPropagation(); setIncludeAdmin(e.target.checked); }}
                  style={{ width: 16, height: 16, cursor: "pointer", accentColor: ST.blue, flexShrink: 0 }} onClick={e => e.stopPropagation()} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Administrative Cost</span>
                <input type="number" min="0" step="0.01" value={adminCost}
                  onChange={e => setAdminCost(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  disabled={!includeAdmin}
                  style={{ ...numInp, opacity: includeAdmin ? 1 : 0.35 }} />
              </div>
              {includeAdmin && (
                <input type="text" placeholder="Override notes (optional)" value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                  style={{ ...numInp, width: "100%", textAlign: "left", fontSize: 12, padding: "6px 10px", marginTop: -4 }} />
              )}

              {/* Jersey */}
              <div style={chkRow(includeJersey)} onClick={() => setIncludeJersey(v => !v)}>
                <input type="checkbox" checked={includeJersey} onChange={e => { e.stopPropagation(); setIncludeJersey(e.target.checked); }}
                  style={{ width: 16, height: 16, cursor: "pointer", accentColor: ST.blue, flexShrink: 0 }} onClick={e => e.stopPropagation()} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Jersey</span>
                <input type="number" min="0" step="0.01" value={jerseyCost}
                  onChange={e => setJerseyCost(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  disabled={!includeJersey}
                  placeholder="0.00"
                  style={{ ...numInp, opacity: includeJersey ? 1 : 0.35 }} />
              </div>

              {/* Tournament */}
              <div style={chkRow(includeTournament)} onClick={() => setIncludeTournament(v => !v)}>
                <input type="checkbox" checked={includeTournament} onChange={e => { e.stopPropagation(); setIncludeTournament(e.target.checked); }}
                  style={{ width: 16, height: 16, cursor: "pointer", accentColor: ST.blue, flexShrink: 0 }} onClick={e => e.stopPropagation()} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Tournament Fee</span>
                <input type="number" min="0" step="0.01" value={tournamentCost}
                  onChange={e => setTournamentCost(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  disabled={!includeTournament}
                  placeholder="0.00"
                  style={{ ...numInp, opacity: includeTournament ? 1 : 0.35 }} />
              </div>
            </div>

            {/* Right: total + pay */}
            <div style={{ width: 180, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ background: isDark ? "rgba(3,78,123,0.12)" : "rgba(3,78,123,0.06)", borderRadius: 10, padding: "12px 14px", flex: 1 }}>
                {prorateAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textMuted, marginBottom: 4 }}><span>Prorated</span><span>${prorateAmt.toFixed(2)}</span></div>}
                {adminAmt   > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textMuted, marginBottom: 4 }}><span>Admin</span><span>${adminAmt.toFixed(2)}</span></div>}
                {jerseyAmt  > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textMuted, marginBottom: 4 }}><span>Jersey</span><span>${jerseyAmt.toFixed(2)}</span></div>}
                {tournamentAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textMuted, marginBottom: 4 }}><span>Tournament</span><span>${tournamentAmt.toFixed(2)}</span></div>}
                <div style={{ height: 1, background: T.divider, margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLabel, textTransform: "uppercase" }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: ST.blue }}>${totalOneTime.toFixed(2)}</span>
                </div>
              </div>
              <input type="text" placeholder="Notes (optional)" value={chargeNotes} onChange={e => setChargeNotes(e.target.value)}
                style={{ ...numInp, width: "100%", textAlign: "left", fontSize: 12, padding: "6px 10px" }} />
              {needCard && (
                <>
                  <div id="billing-cycle-card-el" style={{ padding: "10px 12px", borderRadius: 8, background: T.inputBg, border: `1.5px solid ${T.inputBdr}`, minHeight: 40 }} />
                  <div style={{ fontSize: 10, color: T.textMuted }}>🔒 Stripe — card never stored here.</div>
                </>
              )}
              <PrimaryButton onClick={handlePay} disabled={paying || totalOneTime <= 0}
                style={{ justifyContent: "center" }}>
                <FaDollarSign size={12} /> {paying ? "Processing…" : `Collect $${totalOneTime.toFixed(2)}`}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>

      {/* ── SECTION 2: Recurring Schedule ── */}
      <div style={card()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>Recurring Schedule</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>Charged automatically on its due date — use <strong>Charge Now</strong> below to bill this cycle immediately. See All Transactions for history, or Billing Hub for run-level detail.</div>
          </div>
          <PrimaryButton onClick={() => { setShowSchedForm(v => !v); setEditSchedId(null); }} size="sm" style={{ padding: "6px 14px" }}>
            <FaPlus size={11} /> {showSchedForm ? "Cancel" : "Add Schedule"}
          </PrimaryButton>
        </div>

        {schedMsg && <div style={{ ...toastSt(schedMsg.ok), marginBottom: 10 }}>{schedMsg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {schedMsg.text}</div>}

        {/* Inline add form */}
        {showSchedForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.04)", borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: T.glassBdr }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: "10px 12px", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Frequency</div>
                <select value={schedForm.frequency} onChange={e => {
                    const freq = e.target.value;
                    const netAmt = parseFloat({ monthly: billing?.net_fee_monthly, quarterly: billing?.net_fee_quarterly, semi_annual: billing?.net_fee_semi_annual, annual: billing?.net_fee_annual }[freq] || 0);
                    setSchedForm(f => ({ ...f, frequency: freq, amount: netAmt > 0 ? netAmt.toFixed(2) : f.amount }));
                  }}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 8px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none" }}>
                  {[["monthly","Monthly"],["quarterly","Quarterly"],["semi_annual","Semi-Annual"],["annual","Annual"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Program &amp; Fee</div>
                {(() => {
                  const freq = schedForm.frequency;
                  const GKEY = { monthly:"fee_monthly", quarterly:"fee_quarterly", semi_annual:"fee_semi_annual", annual:"fee_annual" };
                  const NKEY = { monthly:"net_fee_monthly", quarterly:"net_fee_quarterly", semi_annual:"net_fee_semi_annual", annual:"net_fee_annual" };
                  const DISC_TYPE_KEY = { monthly:"disc_monthly_type", quarterly:"disc_quarterly_type", semi_annual:"disc_semi_type", annual:"disc_annual_type" };
                  const DISC_VAL_KEY  = { monthly:"disc_monthly_value", quarterly:"disc_quarterly_value", semi_annual:"disc_semi_value", annual:"disc_annual_value" };

                  const fGross      = parseFloat(billing?.[GKEY[freq]] || 0);
                  const fNet        = parseFloat(billing?.[NKEY[freq]] || fGross);
                  const entType     = billing?.[DISC_TYPE_KEY[freq]];
                  const entVal      = parseFloat(billing?.[DISC_VAL_KEY[freq]] || 0);
                  const studPct     = parseFloat(billing?.discount_pct    || 0);
                  const studFixed   = parseFloat(billing?.discount_amount  || 0);

                  // Compute enterprise discount amount
                  let afterEnterprise = fGross;
                  let entDiscAmt = 0;
                  if (entType === "pct" && entVal > 0) {
                    entDiscAmt = fGross * entVal / 100;
                    afterEnterprise = fGross - entDiscAmt;
                  } else if (entType === "amt" && entVal > 0) {
                    entDiscAmt = entVal;
                    afterEnterprise = fGross - entDiscAmt;
                  }

                  // Compute student discount amount (applied after enterprise)
                  let studDiscAmt = 0;
                  if (studPct > 0) studDiscAmt = afterEnterprise * studPct / 100;
                  else if (studFixed > 0) studDiscAmt = studFixed;

                  const hasEntDisc  = entDiscAmt > 0;
                  const hasStudDisc = studDiscAmt > 0;
                  const hasAnyDisc  = fGross > fNet;

                  // Row helper — theme-safe
                  const row = (label, amt, color, strike) => (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "3px 0" }}>
                      <span style={{ color: color, fontSize: 11 }}>{label}</span>
                      <span style={{ color: color, fontWeight: 600, fontSize: 12, textDecoration: strike ? "line-through" : "none" }}>
                        {amt}
                      </span>
                    </div>
                  );

                  return (
                    <div style={{ padding: "8px 11px", borderRadius: 8, background: T.inputBg, border: `1.5px solid ${T.inputBdr}`, fontSize: 12 }}>
                      {/* Program name */}
                      <div style={{ fontWeight: 700, color: T.textPrimary, fontSize: 12, marginBottom: 6, paddingBottom: 5, borderBottom: `1px solid ${T.divider}` }}>
                        {billing?.program_name || "—"}
                      </div>

                      {/* Gross */}
                      {row(`Gross ${CYCLE_LABELS[freq]}`, `$${fGross.toFixed(2)}`, T.textMuted, hasAnyDisc)}

                      {/* Enterprise discount */}
                      {hasEntDisc
                        ? row(
                            entType === "pct" ? `Enterprise discount (${entVal}%)` : "Enterprise discount",
                            `-$${entDiscAmt.toFixed(2)}`,
                            T.orange, false
                          )
                        : <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", padding: "3px 0" }}>No enterprise discount</div>
                      }

                      {/* Student discount */}
                      {hasStudDisc
                        ? row(
                            studPct > 0 ? `Student discount (${studPct}%)` : "Student discount",
                            `-$${studDiscAmt.toFixed(2)}`,
                            T.green, false
                          )
                        : <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", padding: "3px 0" }}>No student discount</div>
                      }

                      {/* Net total */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                        borderTop: `1.5px solid ${T.divider}`, marginTop: 4, paddingTop: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>
                          Net / {CYCLE_LABELS[freq]}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>
                          ${fNet.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Amount ($)</div>
                <input type="number" min="0" step="0.01" value={schedForm.amount} onChange={e => setSchedForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder={cycleFee.toFixed(2)}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 8px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Next Billing Date</div>
                <input type="date" value={schedForm.next_run_date} onChange={e => setSchedForm(f => ({ ...f, next_run_date: e.target.value }))}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 8px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Period From – To</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input type="date" value={schedForm.program_from} onChange={e => setSchedForm(f => ({ ...f, program_from: e.target.value }))}
                    style={{ flex: 1, border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 6px", background: T.inputBg, color: T.inputText, fontSize: 12, outline: "none" }} />
                  <input type="date" value={schedForm.program_to} onChange={e => setSchedForm(f => ({ ...f, program_to: e.target.value }))}
                    style={{ flex: 1, border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 6px", background: T.inputBg, color: T.inputText, fontSize: 12, outline: "none" }} />
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <PrimaryButton onClick={handleAddSchedule} size="sm" style={{ padding: "6px 16px" }}><FaSave size={11} /> Save Schedule</PrimaryButton>
              <GhostButton onClick={() => setShowSchedForm(false)} style={{ padding: "6px 14px" }}>Cancel</GhostButton>
            </div>
          </div>
        )}

        {/* Schedule table */}
        {(data?.schedules || []).length === 0 && !showSchedForm ? (
          <div style={{ textAlign: "center", padding: "20px 0", color: T.textMuted, fontSize: 13 }}>
            <FaRegClock size={22} style={{ opacity: 0.25, marginBottom: 6 }} />
            <div>No recurring schedules yet. Click <strong>Add Schedule</strong> to set one up.</div>
          </div>
        ) : (data?.schedules || []).length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th key="frequency" onClick={() => schedTableSort.toggleSort("frequency")} style={{ padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: "0.06em", ...schedTableSort.thSort("frequency", T),
                    color: schedTableSort.sortKey === "frequency" ? T.blue : T.textPrimary,
                    borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" }}>Frequency{schedTableSort.sortArrow("frequency")}</th>
                  <th style={{ padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: "0.06em", color: T.textPrimary,
                    borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" }}>Program</th>
                  {[["Amount","amount"],["Next Billing","next_run_date"],
                    ["Period From","program_from"],["Period To","program_to"],["Status","active"]].map(([h, key]) => (
                    <th key={h} onClick={() => schedTableSort.toggleSort(key)} style={{ padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                      textTransform: "uppercase", letterSpacing: "0.06em", ...schedTableSort.thSort(key, T),
                      color: schedTableSort.sortKey === key ? T.blue : T.textPrimary,
                      borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" }}>{h}{schedTableSort.sortArrow(key)}</th>
                  ))}
                  <th style={{ padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: "0.06em", color: T.textPrimary,
                    borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" }}></th>
                </tr>
              </thead>
              <tbody>
                {schedTableSort.sorted.map((s, i) => {
                  const isEditing = editSchedId === s.schedule_id;
                  const tdSt = { padding: "8px 10px", borderBottom: `1px solid ${T.divider}`, verticalAlign: "middle" };
                  const miniInp = { border: `1.5px solid ${T.inputBdr}`, borderRadius: 6, padding: "4px 6px", background: T.inputBg, color: T.inputText, fontSize: 12, outline: "none", width: "100%" };
                  return (
                    <tr key={s.schedule_id} style={{ background: i % 2 === 0 ? "transparent" : T.glass }}>
                      <td style={tdSt}>
                        {isEditing
                          ? <select value={editSchedForm.frequency} onChange={e => setEditSchedForm(f => ({ ...f, frequency: e.target.value }))} style={miniInp}>
                              {[["monthly","Monthly"],["quarterly","Quarterly"],["semi_annual","Semi-Annual"],["annual","Annual"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          : <span style={{ color: T.textPrimary, fontWeight: 600 }}>{CYCLE_LABELS[s.frequency] || s.frequency}</span>
                        }
                      </td>
                      <td style={tdSt}>
                        <div style={{ fontSize: 12, color: T.textSub }}>{billing?.program_name || "—"}</div>
                      </td>
                      <td style={tdSt}>
                        {isEditing
                          ? <input type="number" min="0" step="0.01" value={editSchedForm.amount} onChange={e => setEditSchedForm(f => ({ ...f, amount: e.target.value }))} style={{ ...miniInp, width: 80 }} />
                          : <span style={{ color: ST.blue, fontWeight: 700 }}>${parseFloat(s.amount || 0).toFixed(2)}</span>
                        }
                      </td>
                      <td style={tdSt}>
                        {isEditing
                          ? <input type="date" value={editSchedForm.next_run_date} onChange={e => setEditSchedForm(f => ({ ...f, next_run_date: e.target.value }))} style={miniInp} />
                          : <span style={{ color: T.textSub }}>{fmtDate(s.next_run_date, dateFmt)}</span>
                        }
                      </td>
                      <td style={tdSt}>
                        {isEditing
                          ? <input type="date" value={editSchedForm.program_from} onChange={e => setEditSchedForm(f => ({ ...f, program_from: e.target.value }))} style={miniInp} />
                          : <span style={{ color: T.textMuted, fontSize: 12 }}>{fmtDate(s.program_from, dateFmt)}</span>
                        }
                      </td>
                      <td style={tdSt}>
                        {isEditing
                          ? <input type="date" value={editSchedForm.program_to} onChange={e => setEditSchedForm(f => ({ ...f, program_to: e.target.value }))} style={miniInp} />
                          : <span style={{ color: T.textMuted, fontSize: 12 }}>{fmtDate(s.program_to, dateFmt)}</span>
                        }
                      </td>
                      <td style={tdSt}>
                        <span style={{ padding: "2px 9px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: s.active ? T.greenBg : T.glass, color: s.active ? T.green : T.textMuted,
                          border: `1px solid ${s.active ? T.green + "44" : T.divider}` }}>
                          {s.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ ...tdSt, whiteSpace: "nowrap" }}>
                        {isEditing ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            <PrimaryButton onClick={handleUpdateSchedule} size="sm" style={{ padding: "3px 10px" }}><FaSave size={10} /></PrimaryButton>
                            <GhostButton onClick={() => setEditSchedId(null)} style={{ padding: "3px 10px", fontSize: 11 }}>✕</GhostButton>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6 }}>
                            {s.active === 1 && (
                              <PrimaryButton onClick={() => chargeScheduleNow(s.schedule_id)} disabled={chargingSchedId === s.schedule_id}
                                title="Manually charge this student's saved card for the current cycle right now"
                                size="sm" style={{ padding: "3px 10px" }}>
                                <FaDollarSign size={10} /> {chargingSchedId === s.schedule_id ? "…" : "Charge Now"}
                              </PrimaryButton>
                            )}
                            <GhostButton onClick={() => startEditSched(s)} style={{ padding: "3px 10px", fontSize: 11 }}><FaEdit size={10} /></GhostButton>
                            <DangerButton onClick={() => deleteSchedule(s.schedule_id)} style={{ padding: "3px 10px", fontSize: 11 }}><FaTrash size={10} /></DangerButton>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Read-only: this person's OWN lane-membership recurring schedule,
            if they're also (or were converted from) a lane client. This is
            a genuinely separate billing relationship in a separate table
            (lb_client_recurring_schedules) — shown here only so an admin
            never has to leave this tab to know it exists; editing/charging
            it still happens from the Lane Client screens. */}
        {(data?.lane_schedules || []).length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${T.divider}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3b82f6", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              Lane Membership Schedule (separate billing — read-only here)
            </div>
            {data.lane_schedules.map(ls => (
              <div key={ls.schedule_id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6,
                border: `1px solid rgba(59,130,246,0.25)`, background: "rgba(59,130,246,0.05)",
                borderRadius: 9, padding: "8px 12px", marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 800, color: T.textPrimary }}>{ls.program_name || `Plan #${ls.plan_id}`}</span>
                  <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 8 }}>
                    {CYCLE_LABELS[ls.frequency] || ls.frequency} · Next billing {fmtDate(ls.next_run_date, dateFmt)}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#3b82f6", fontWeight: 700, fontSize: 12 }}>${parseFloat(ls.amount || 0).toFixed(2)}</span>
                  <span style={{ padding: "2px 9px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: ls.active ? T.greenBg : T.glass, color: ls.active ? T.green : T.textMuted,
                    border: `1px solid ${ls.active ? T.green + "44" : T.divider}` }}>
                    {ls.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Ad-hoc Charges ── */}
      <div style={card()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>Ad-Hoc Charges</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>One-off charges outside the regular billing schedule.</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {adhocCharges.some(c => c.status === "paid") && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: T.greenBg, color: T.green, border: `1px solid ${T.green}44` }}>Charges Recorded</span>}
            <PrimaryButton onClick={() => setShowAdhocForm(v => !v)} size="sm" style={{ padding: "5px 12px" }}>
              <FaPlus size={10} /> {showAdhocForm ? "Cancel" : "Ad-hoc Charge"}
            </PrimaryButton>
          </div>
        </div>

        {/* Ad-hoc charge history */}
        {adhocCharges.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: showAdhocForm ? 12 : 0 }}>
            {adhocCharges.map(c => {
              const cid     = c.charge_id;
              const isPaid  = c.status === "paid";
              const cmsg    = collectMsg[cid];
              const busy    = collectingId === cid;
              return (
                <div key={cid} style={{ borderRadius: 8, overflow: "hidden",
                  border: `1px solid ${isPaid ? T.green + "33" : T.divider}` }}>
                  {/* Main row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: isPaid ? T.greenBg : T.glass, padding: "8px 14px" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace",
                        color: isPaid ? T.green : T.textMuted }}>{c.billing_ref}</span>
                      <span style={{ fontSize: 12, color: T.textMuted }}>{fmtDate(c.charge_date, dateFmt)}</span>
                      {c.notes && <span style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>{c.notes}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: isPaid ? T.greenBg : T.orangeBg,
                        color: isPaid ? T.green : T.orange,
                        border: `1px solid ${isPaid ? T.green + "44" : T.orange + "44"}` }}>
                        {isPaid ? "Paid ✓" : "Pending"}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: isPaid ? T.green : T.textPrimary }}>
                        ${parseFloat(c.total_amount).toFixed(2)}
                      </span>
                      {!isPaid && (
                        <>
                          <button onClick={() => handleCollectCharge(cid, "stripe")} disabled={busy}
                            title="Charge parent's saved Stripe card"
                            style={{ padding: "4px 12px", borderRadius: 7, border: "none", cursor: "pointer",
                              background: ST.green, color: "#fff", fontSize: 11, fontWeight: 700,
                              opacity: busy ? 0.6 : 1 }}>
                            {busy ? "…" : "Stripe Pay"}
                          </button>
                          <button onClick={() => setShowManualRow(s => ({ ...s, [cid]: !s[cid] }))}
                            title="Mark as paid manually (cash / cheque)"
                            style={{ padding: "4px 10px", borderRadius: 7, cursor: "pointer",
                              background: T.glass, border: `1px solid ${T.divider}`,
                              color: T.textSub, fontSize: 11 }}>
                            Manual
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Manual payment row */}
                  {!isPaid && showManualRow[cid] && (
                    <div style={{ padding: "8px 14px", background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.03)",
                      borderTop: `1px solid ${T.divider}`, display: "flex", gap: 8, alignItems: "center" }}>
                      <input value={manualRefRow[cid] || ""} placeholder="Reference — e.g. Cash, Cheque #1234, Bank Transfer"
                        onChange={e => setManualRefRow(m => ({ ...m, [cid]: e.target.value }))}
                        style={{ flex: 1, padding: "6px 10px", borderRadius: 7, fontSize: 12,
                          border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, outline: "none" }} />
                      <button onClick={() => handleCollectCharge(cid, "manual")} disabled={busy}
                        style={{ padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer",
                          background: ST.yellow, color: "#1a2840", fontSize: 12, fontWeight: 700,
                          opacity: busy ? 0.6 : 1 }}>
                        {busy ? "Saving…" : "Mark as Paid"}
                      </button>
                    </div>
                  )}
                  {/* Feedback message */}
                  {cmsg && (
                    <div style={{ padding: "5px 14px", fontSize: 12,
                      color: cmsg.ok ? ST.green : ST.red,
                      background: cmsg.ok ? `${ST.green}10` : `${ST.red}10`,
                      borderTop: `1px solid ${T.divider}` }}>
                      {cmsg.ok ? "✓" : "✗"} {cmsg.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Ad-hoc charge form */}
        {showAdhocForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.04)", borderRadius: 10, padding: "14px 16px", border: T.glassBdr }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLabel, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>New Ad-hoc Charge</div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Description</div>
                <input type="text" placeholder="e.g. Extra coaching session, Equipment fee…" value={adhocDesc} onChange={e => setAdhocDesc(e.target.value)}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "8px 10px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ width: 120 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Amount ($)</div>
                <input type="number" min="0" step="0.01" placeholder="0.00" value={adhocAmt} onChange={e => setAdhocAmt(e.target.value)}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "8px 10px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", boxSizing: "border-box", textAlign: "right" }} />
              </div>
              <PrimaryButton onClick={handleAdhocPay} disabled={adhocPaying || !adhocDesc.trim() || !adhocAmt}
                style={{ padding: "8px 18px", whiteSpace: "nowrap" }}>
                <FaDollarSign size={12} /> {adhocPaying ? "Processing…" : `Collect${adhocAmt ? ` $${parseFloat(adhocAmt || 0).toFixed(2)}` : ""}`}
              </PrimaryButton>
            </div>
            {needCard && (
              <div style={{ marginTop: 10 }}>
                <div id="billing-cycle-card-el" style={{ padding: "10px 12px", borderRadius: 8, background: T.inputBg, border: `1.5px solid ${T.inputBdr}`, minHeight: 40 }} />
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>🔒 Stripe — card never stored here.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── WAIVER TAB ───────────────────────────────────────────────────────────────
function WaiverTab({ studentId, currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const [waivers,    setWaivers]    = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [file,       setFile]       = useState(null);
  const [form,       setForm]       = useState({ template_id: "", signed_by: "", signed_on: new Date().toISOString().split("T")[0] });
  const [msg,        setMsg]        = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wr, tr] = await Promise.all([
        fetch(`${BASE}/student-billing/${studentId}/waivers`).then(r => r.json()),
        fetch(`${BASE}/waivers/templates`).then(r => r.json()),
      ]);
      setWaivers(wr.waivers || []);
      const allTmpl = [tr.active, ...(tr.history || [])].filter(Boolean);
      setTemplates(allTmpl);
      // Pre-select active template
      if (!form.template_id && tr.active) setForm(f => ({ ...f, template_id: String(tr.active.template_id) }));
    } catch { setWaivers([]); } finally { setLoading(false); }
  }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) { setMsg({ ok: false, text: "Please select a signed PDF to upload." }); return; }
    if (!form.signed_by.trim()) { setMsg({ ok: false, text: "Signed By is required." }); return; }
    setUploading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("signed_by", form.signed_by);
      fd.append("signed_on", form.signed_on || "");
      fd.append("template_id", form.template_id || "");
      const r = await fetch(`${BASE}/student-billing/${studentId}/waivers/upload-pdf`, { method: "POST", body: fd });
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: true, text: "Signed waiver uploaded." });
        setShowForm(false); setFile(null);
        setForm({ template_id: form.template_id, signed_by: "", signed_on: new Date().toISOString().split("T")[0] });
        load();
      } else { setMsg({ ok: false, text: d.message || "Upload failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    finally { setUploading(false); }
  };

  const handleDelete = async (wId) => {
    if (!window.confirm("Remove this waiver record?")) return;
    const r = await fetch(`${BASE}/student-billing/${studentId}/waivers/${wId}`, { method: "DELETE" });
    const d = await r.json();
    if (d.success) load(); else setMsg({ ok: false, text: d.message || "Failed." });
  };

  const card = { background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, border: `1px solid ${T.divider}`, borderRadius: 14, padding: "18px 20px", marginBottom: 16 };
  const inp  = { border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 10px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div>
      {msg && <div style={{ ...toastSt(msg.ok), marginBottom: 16 }}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      <div style={card}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showForm || waivers.length > 0 ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>Signed Waivers</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>Consent forms signed by the parent/guardian.</div>
          </div>
          <PrimaryButton onClick={() => { setShowForm(v => !v); setMsg(null); setFile(null); }}
            size="sm" style={{ padding: "5px 12px" }}>
            <FaPlus size={10} /> {showForm ? "Cancel" : "Upload Signed PDF"}
          </PrimaryButton>
        </div>

        {/* Upload form */}
        {showForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.04)", borderRadius: 10, padding: "14px 16px", marginBottom: waivers.length > 0 ? 12 : 0, border: `1px solid ${T.inputBdr}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Waiver Template</div>
                <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} style={{ ...inp, appearance: "none" }}>
                  <option value="">— Select template —</option>
                  {templates.map(t => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.waiver_name} ({t.version}){t.is_active ? " ★ Active" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Signed By *</div>
                <input style={inp} value={form.signed_by} onChange={e => setForm(f => ({ ...f, signed_by: e.target.value }))} placeholder="Parent / guardian full name" />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Date Signed</div>
                <input type="date" style={inp} value={form.signed_on} onChange={e => setForm(f => ({ ...f, signed_on: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => setFile(e.target.files?.[0] || null)} />
              <button onClick={() => fileRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 7,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", background: T.glass,
                  border: `1.5px solid ${T.inputBdr}`, color: T.textPrimary }}>
                <FaUpload size={11} /> {file ? file.name : "Choose Signed PDF"}
              </button>
              <button onClick={handleUpload} disabled={uploading}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 7,
                  fontSize: 12, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer", border: "none",
                  background: uploading ? "#94a3b8" : ST.green, color: "#fff" }}>
                <FaSave size={11} /> {uploading ? "Uploading…" : "Save Waiver"}
              </button>
            </div>
          </div>
        )}

        {/* Waiver list */}
        {loading ? (
          <div style={{ padding: "16px 0", textAlign: "center", color: T.textMuted, fontSize: 13 }}>Loading…</div>
        ) : waivers.length === 0 && !showForm ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted, fontSize: 13 }}>
            <FaFileSignature size={24} style={{ opacity: 0.25, marginBottom: 8 }} />
            <div>No signed waivers on record. Upload a signed PDF to add one.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {waivers.map((w) => {
              const hasPdf      = !!w.signed_pdf_url;
              const isParentSig = w.source === "parent_portal";
              const hasSig      = isParentSig && !!w.signature_url;
              const isGreen     = hasPdf || isParentSig;
              const syntheticId = typeof w.waiver_id === "string" && w.waiver_id.startsWith("pp_");
              return (
                <div key={w.waiver_id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  background: isGreen
                    ? (isDark ? "rgba(5,150,105,0.10)" : "#dcfce7")
                    : (isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.03)"),
                  borderRadius: 8, padding: "9px 14px",
                  border: `1px solid ${isGreen ? "rgba(5,150,105,0.25)" : T.divider}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                    <FaFileSignature size={14} color={isGreen ? T.green : T.textMuted} style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {w.waiver_name || w.template_name || "Member Waiver"}
                        {(w.version || w.template_version) && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 9,
                            background: isGreen ? T.greenBg : (isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.08)"),
                            color: isGreen ? T.green : T.textSub,
                            border: `1px solid ${isGreen ? T.green + "44" : T.divider}` }}>
                            {w.version || w.template_version}
                          </span>
                        )}
                        {isParentSig && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 9,
                            background: isDark ? "rgba(59,130,246,0.15)" : "#dbeafe",
                            color: "#3b82f6", border: "1px solid rgba(59,130,246,0.30)" }}>
                            Parent Portal
                          </span>
                        )}
                        {hasPdf && <span style={{ fontSize: 10, fontWeight: 700, color: T.green }}>✓ PDF</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                        {w.signed_by && <span>Signed by <strong>{w.signed_by}</strong></span>}
                        {w.signed_on && <span style={{ marginLeft: 8 }}>{fmtDate(w.signed_on, dateFmt)}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {hasPdf && (
                      <>
                        <a href={`${BASE}${w.signed_pdf_url}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6,
                            fontSize: 11, fontWeight: 700, textDecoration: "none",
                            background: T.greenBg, color: T.green, border: `1px solid ${T.green}44` }}>
                          <FaFileAlt size={9} /> View PDF
                        </a>
                        <a href={`${BASE}${w.signed_pdf_url}`}
                          download={w.signed_pdf_name || `signed_waiver_${w.waiver_id}.pdf`}
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6,
                            fontSize: 11, fontWeight: 700, textDecoration: "none",
                            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.07)",
                            color: T.textSub, border: `1px solid ${T.divider}` }}>
                          <FaDownload size={9} /> Download
                        </a>
                      </>
                    )}
                    {hasSig && !hasPdf && (
                      <a href={`${BASE}${w.signature_url}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6,
                          fontSize: 11, fontWeight: 700, textDecoration: "none",
                          background: T.greenBg, color: T.green, border: `1px solid ${T.green}44` }}>
                        <FaFileSignature size={9} /> View Signature
                      </a>
                    )}
                    {w.document_url && !hasPdf && !hasSig && (
                      <a href={w.document_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6,
                          fontSize: 11, fontWeight: 700, textDecoration: "none",
                          background: T.glass, color: T.textSub, border: `1px solid ${T.divider}` }}>
                        <FaFileAlt size={9} /> View
                      </a>
                    )}
                    {!syntheticId && (
                      <DangerButton onClick={() => handleDelete(w.waiver_id)}
                        style={{ padding: "4px 9px", fontSize: 11 }}>
                        <FaTrash size={9} />
                      </DangerButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NOTES TAB ───────────────────────────────────────────────────────────────
function NotesTab({ studentId, currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [form,     setForm]     = useState({ note_date: new Date().toISOString().split("T")[0], note_text: "" });
  const [msg,      setMsg]      = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/student-billing/${studentId}/notes`);
      const d = await r.json();
      setNotes(d.notes || []);
    } catch { setNotes([]); } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const saveNote = async () => {
    if (!form.note_text.trim()) { setMsg({ ok: false, text: "Note text is required." }); return; }
    try {
      const url    = editNote ? `${BASE}/student-billing/notes/${editNote.note_id}` : `${BASE}/student-billing/${studentId}/notes`;
      const method = editNote ? "PUT" : "POST";
      const r      = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, updated_by: currentUserId || "admin", created_by: currentUserId || "admin" }) });
      const d      = await r.json();
      if (d.success) { setMsg({ ok: true, text: editNote ? "Note updated." : "Note added." }); setShowForm(false); setEditNote(null); setForm({ note_date: new Date().toISOString().split("T")[0], note_text: "" }); load(); }
      else setMsg({ ok: false, text: d.message || "Failed." });
    } catch { setMsg({ ok: false, text: "Network error." }); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await fetch(`${BASE}/student-billing/notes/${id}`, { method: "DELETE" }); load(); } catch {}
  };

  const startEdit = (n) => { setEditNote(n); setForm({ note_date: n.note_date || "", note_text: n.note_text || "" }); setShowForm(true); };
  const cancelForm = () => { setShowForm(false); setEditNote(null); setForm({ note_date: new Date().toISOString().split("T")[0], note_text: "" }); };

  const card = { background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur, border: `1px solid ${T.divider}`, borderRadius: 14, padding: "18px 20px", marginBottom: 16 };

  return (
    <div>
      {msg && <div style={{ ...toastSt(msg.ok), marginBottom: 16 }}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      <div style={card}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showForm || notes.length > 0 ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary }}>Staff Notes</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>Internal notes visible to staff only.</div>
          </div>
          <PrimaryButton onClick={() => { if (showForm && !editNote) { cancelForm(); } else { setShowForm(true); setEditNote(null); setForm({ note_date: new Date().toISOString().split("T")[0], note_text: "" }); } }}
            size="sm" style={{ padding: "5px 12px" }}>
            <FaPlus size={10} /> {showForm && !editNote ? "Cancel" : "Add Note"}
          </PrimaryButton>
        </div>

        {/* Inline add/edit form */}
        {showForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.04)", borderRadius: 10, padding: "14px 16px", marginBottom: notes.length > 0 ? 12 : 0, border: `1px solid ${T.inputBdr}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Note Date</div>
                <input type="date" value={form.note_date} onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 8px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Note</div>
                <textarea value={form.note_text} onChange={e => setForm(f => ({ ...f, note_text: e.target.value }))}
                  placeholder="Enter note here…" rows={2}
                  style={{ width: "100%", border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "6px 10px", background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: ST.font }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <PrimaryButton onClick={saveNote} size="sm" style={{ padding: "5px 14px" }}><FaSave size={11} /> {editNote ? "Update" : "Save Note"}</PrimaryButton>
              <GhostButton onClick={cancelForm} style={{ padding: "5px 12px", fontSize: 12 }}><FaTimesCircle size={11} /> Cancel</GhostButton>
            </div>
          </div>
        )}

        {/* Note list */}
        {loading ? (
          <div style={{ padding: "16px 0", textAlign: "center", color: T.textMuted, fontSize: 13 }}>Loading…</div>
        ) : notes.length === 0 && !showForm ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted, fontSize: 13 }}>
            <FaStickyNote size={22} style={{ marginBottom: 8, opacity: 0.3 }} />
            <div>No notes yet. Click <strong>Add Note</strong> to add one.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {notes.map(n => (
              <div key={n.note_id} style={{
                display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
                background: editNote?.note_id === n.note_id ? (isDark ? "rgba(255,200,0,0.07)" : "rgba(255,200,0,0.10)") : (isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.03)"),
                borderRadius: 8, padding: "9px 12px",
                border: `1px solid ${editNote?.note_id === n.note_id ? "rgba(255,200,0,0.35)" : T.divider}`
              }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                  {n.note_date && (
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: T.textSub, whiteSpace: "nowrap", paddingTop: 1 }}>
                      {fmtDate(n.note_date, dateFmt)}
                    </span>
                  )}
                  <span style={{ fontSize: 13, color: T.textPrimary, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.note_text}</span>
                </div>
                <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                  <GhostButton onClick={() => startEdit(n)} style={{ padding: "3px 9px", fontSize: 11 }}><FaPencilAlt size={9} /></GhostButton>
                  <DangerButton onClick={() => deleteNote(n.note_id)} style={{ padding: "3px 9px", fontSize: 11 }}><FaTrash size={9} /></DangerButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ATTENDANCE TAB ───────────────────────────────────────────────────────────
function AttendanceTab({ studentId }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const attendanceSort = useSort(records, "attendance_date", "desc");

  useEffect(() => {
    fetch(`${BASE}/student-billing/${studentId}/attendance`)
      .then(r => r.json()).then(d => setRecords(d.attendance || [])).catch(() => {}).finally(() => setLoading(false));
  }, [studentId]);

  const statusColor = (s) => ({ Present: ST.green, Absent: ST.red, Late: ST.orange, Excused: "#a78bfa" }[s] || "#94a3b8");

  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>;

  // Summary counts
  const counts = records.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  const sorted = attendanceSort.sorted;

  const thStyle = { padding: "10px 12px", textAlign: "left", fontWeight: 800, fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: T.textPrimary, borderBottom: `1.5px solid ${T.glassBdr}` };

  return (
    <div>
      {/* Summary pills */}
      {records.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {Object.entries(counts).map(([status, count]) => (
            <div key={status} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 20, background: statusColor(status) + "14", border: `1.5px solid ${statusColor(status)}44` }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: statusColor(status) }}>{count}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: statusColor(status) }}>{status}</span>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 20, background: "rgba(3,78,123,0.08)", border: "1.5px solid rgba(3,78,123,0.20)" }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary }}>{records.length}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textSub }}>Total</span>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: T.textMuted, fontSize: 14 }}>
          <FaClipboardList size={28} style={{ marginBottom: 10, opacity: 0.3 }} />
          <div>No attendance records for this student.</div>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(0,53,128,0.07)" }}>
                <th style={{ ...thStyle, cursor: "pointer", userSelect: "none" }} onClick={() => attendanceSort.toggleSort("attendance_date")}>
                  Date{attendanceSort.sortArrow("attendance_date")}
                </th>
                {[["Batch","batch_name"], ["Status","status"], ["Check-in Method","check_in_method"], ["Marked By","marked_by"]].map(([h,key]) => (
                  <th key={h} style={{ ...thStyle, cursor: "pointer", userSelect: "none" }} onClick={() => attendanceSort.toggleSort(key)}>{h}{attendanceSort.sortArrow(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.attendance_id} style={{ background: i % 2 === 0 ? "transparent" : "rgba(0,53,128,0.03)" }}>
                  <td style={{ padding: "9px 12px", color: T.textPrimary, fontWeight: 600, borderBottom: `1px solid ${T.glassBdr}` }}>{fmtDate(r.attendance_date, dateFmt)}</td>
                  <td style={{ padding: "9px 12px", color: T.textSub, borderBottom: `1px solid ${T.glassBdr}` }}>{r.batch_name || "—"}</td>
                  <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.glassBdr}` }}>
                    <span style={{ display: "inline-flex", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, color: statusColor(r.status), background: statusColor(r.status) + "18", border: `1px solid ${statusColor(r.status)}44` }}>{r.status}</span>
                  </td>
                  <td style={{ padding: "9px 12px", color: T.textSub, borderBottom: `1px solid ${T.glassBdr}` }}>{r.check_in_method || "—"}</td>
                  <td style={{ padding: "9px 12px", color: T.textSub, borderBottom: `1px solid ${T.glassBdr}` }}>{r.marked_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── CREATE STUDENT FORM (inline, no modal wrapper) ──────────────────────────
function CreateStudentForm({ currentUserId, onCreated }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const today   = new Date().toISOString().split("T")[0];
  const fileRef = useRef(null);
  const [form,         setForm]         = useState(blankForm(today));
  const [errors,       setErrors]       = useState({});
  const [msg,          setMsg]          = useState(null);
  const [skillLevels,  setSkillLevels]  = useState([]);
  const [playingRoles, setPlayingRoles] = useState([]);

  useEffect(() => {
    fetch(`${BASE}/students/skill-levels`).then(r => r.json()).then(d => setSkillLevels(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/students/playing-roles`).then(r => r.json()).then(d => setPlayingRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const rolesByGroup = playingRoles.reduce((acc, r) => { (acc[r.role_group] = acc[r.role_group] || []).push(r); return acc; }, {});

  const handleChange = (e) => {
    const { name, value, files, checked } = e.target;
    if (name === "photo_file")       { setForm(f => ({ ...f, photo_file: files?.[0] || null })); return; }
    if (name === "tournament_ready") { setForm(f => ({ ...f, tournament_ready: checked }));       return; }
    if (name === "parent_phone")     { setForm(f => ({ ...f, parent_phone: formatPhone(value) })); return; }
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.first_name.trim())  newErrors.first_name    = "Required";
    if (!form.last_name.trim())   newErrors.last_name     = "Required";
    if (!form.date_of_birth)      newErrors.date_of_birth = "Required";
    if (!form.gender)             newErrors.gender        = "Required";
    if (!form.parent_name.trim()) newErrors.parent_name   = "Required";
    if (!form.parent_email || !form.parent_email.includes("@")) newErrors.parent_email = "Valid email required";
    if (form.parent_phone.replace(/\D/g, "").length !== 10) newErrors.parent_phone = "10 digits required";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    const payload = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "photo_file") return;
      if (k === "tournament_ready") { payload.append(k, v ? "true" : "false"); return; }
      if (k === "parent_phone") payload.append(k, v.replace(/\D/g, ""));
      else payload.append(k, v ?? "");
    });
    payload.append("updated_by", currentUserId ? String(currentUserId) : "admin");
    if (form.photo_file) payload.append("photo_file", form.photo_file);

    try {
      const res  = await fetch(`${BASE}/students/`, { method: "POST", body: payload });
      const data = await res.json();
      const newId = data.student_id ?? data.student?.student_id;
      if (data.success && newId) {
        onCreated(newId);
      } else {
        setMsg({ ok: false, text: data.message || "Error saving student" });
      }
    } catch { setMsg({ ok: false, text: "Network error" }); }
  };

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}
      <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
        <SectionLabel>Student Info</SectionLabel>
        <Field label="First Name" icon={<FaUser />} name="first_name" value={form.first_name} onChange={handleChange} error={errors.first_name} />
        <Field label="Last Name"  icon={<FaUser />} name="last_name"  value={form.last_name}  onChange={handleChange} error={errors.last_name} />
        <Field label={<>Date of Birth <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaCalendar />} name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} error={errors.date_of_birth} />
        <SelectField label="Gender" icon={<FaTransgender />} name="gender" value={form.gender} onChange={handleChange} options={[["","Select gender"],["Male","Male"],["Female","Female"],["Other","Other"]]} error={errors.gender} />
        <Field label={<>Join Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaCalendar />} name="join_date" type="date" value={form.join_date} onChange={handleChange} />
        <SelectField label="Skill Level" icon={<FaGraduationCap />} name="skill_level_id" value={form.skill_level_id} onChange={handleChange} options={[["","Select skill level"],...skillLevels.map(l=>[String(l.level_id),l.level_name])]} />
        <AgeCategoryField dobStr={form.date_of_birth} />
        <PlayingRoleSelect name="playing_role_id" value={form.playing_role_id} onChange={handleChange} rolesByGroup={rolesByGroup} />

        <SectionLabel>Tournament &amp; Jersey</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <label style={labelSt(T)}>Tournament Ready</label>
          <div onClick={() => setForm(f => ({ ...f, tournament_ready: !f.tournament_ready }))} style={{ ...inputWrapSt(T), gap: "12px", cursor: "pointer", border: form.tournament_ready ? "1.5px solid rgba(5,150,105,0.55)" : `1.5px solid ${T.inputBdr}`, background: form.tournament_ready ? "rgba(5,150,105,0.07)" : T.inputBg }}>
            <FaTrophy style={{ color: form.tournament_ready ? ST.green : T.textMuted, flexShrink: 0 }} />
            <input type="checkbox" name="tournament_ready" checked={form.tournament_ready} onChange={handleChange} style={{ accentColor: ST.green, width: "16px", height: "16px", cursor: "pointer" }} onClick={e => e.stopPropagation()} />
            <span style={{ fontSize: "14px", color: form.tournament_ready ? ST.green : T.textSub, fontWeight: form.tournament_ready ? 700 : 500 }}>{form.tournament_ready ? "Yes — tournament eligible" : "Not yet tournament ready"}</span>
          </div>
        </div>
        <Field label={<>Jersey Purchased Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>} icon={<FaTshirt />} name="jersey_purchased_date" type="date" value={form.jersey_purchased_date} onChange={handleChange} />

        <SectionLabel>Parent / Guardian</SectionLabel>
        <Field label={<>Parent Name <span style={lightText(T)}>(First & Last)</span></>} icon={<FaUserFriends />} name="parent_name" value={form.parent_name} onChange={handleChange} error={errors.parent_name} />
        <Field label="Parent Email" icon={<FaEnvelope />} name="parent_email" value={form.parent_email} onChange={handleChange} error={errors.parent_email} />
        <Field label={<>Phone <span style={lightText(T)}>(xxx-xxx-xxxx)</span></>} icon={<FaPhone />} name="parent_phone" value={form.parent_phone} onChange={handleChange} maxLength={12} error={errors.parent_phone} />
        <div>
          <label style={labelSt(T)}>Upload Photo</label>
          <div style={inputWrapSt(T)}>
            <FaImage style={{ marginRight: "10px", color: T.textMuted, flexShrink: 0 }} />
            <input type="file" name="photo_file" accept="image/*" onChange={handleChange} style={{ fontSize: "13px" }} ref={fileRef} />
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
          <PrimaryButton type="submit" style={{ width: "100%", padding: "13px", fontSize: 14 }}>
            <FaSave size={15} /> Create Student &amp; Unlock All Tabs
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

// ─── LOCKED TAB PLACEHOLDER (create mode, other tabs) ────────────────────────
function LockedTabPlaceholder({ tabLabel, onGoToDetails }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div style={{ textAlign: "center", padding: "52px 32px" }}>
      <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(0,53,128,0.07)", border: "2px dashed rgba(0,53,128,0.20)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
        <FaIdCard size={22} style={{ color: ST.blue, opacity: 0.4 }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: ST.blue, marginBottom: 8 }}>{tabLabel} — available after saving</div>
      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7, maxWidth: 340, margin: "0 auto 24px" }}>
        Complete the <strong style={{ color: ST.blue }}>Details</strong> tab to create this student first.
        Once saved, all tabs will unlock with the student's full profile.
      </div>
      <PrimaryButton onClick={onGoToDetails} style={{ display: "inline-flex", padding: "10px 28px" }}>
        <FaUserEdit size={13} /> Go to Details
      </PrimaryButton>
    </div>
  );
}

// ─── BULK UPLOAD MODAL ────────────────────────────────────────────────────────
function BulkUploadModal({ onClose }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const fileRef = useRef(null);
  const [file,     setFile]    = useState(null);
  const [report,   setReport]  = useState(null);
  const [errorMsg, setErrorMsg]= useState("");
  const [loading,  setLoading] = useState(false);
  const bulkModalSort = useSort(report?.rows || [], null);

  const handleUpload = async () => {
    setErrorMsg(""); setReport(null);
    if (!file) { setErrorMsg("Please select an Excel file first."); return; }
    const fd = new FormData(); fd.append("file", file);
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/students/bulk/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (fileRef.current) fileRef.current.value = "";
      setFile(null);
      if (data.error) setErrorMsg(data.error);
      else if (data.rows?.length > 0) setReport(data);
      else setErrorMsg("No data was imported.");
    } catch { setErrorMsg("System error."); } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" , WebkitBackdropFilter: "blur(4px)"}}>
      <div style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto", margin: "0 16px", ...glassCard(T), position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: ST.blue, display: "flex", alignItems: "center", gap: 10 }}><FaUpload size={16} /> Bulk Upload Students</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted }}><FaTimes size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <GhostButton onClick={() => window.open(`${BASE}/students/bulk/template`)}><FaDownload size={12} /> Download Template</GhostButton>
          <div style={{ ...inputWrapSt(T), flex: 1, minWidth: 200 }}>
            <FaUpload style={{ color: T.textMuted, flexShrink: 0, marginRight: 8 }} />
            <input ref={fileRef} type="file" accept=".xlsx" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13, flex: 1 }} />
          </div>
          <PrimaryButton onClick={handleUpload} disabled={loading}><FaUpload size={12} /> {loading ? "Uploading…" : "Upload"}</PrimaryButton>
        </div>
        {errorMsg && <div style={toastSt(false)}><FaExclamationCircle /> {errorMsg}</div>}
        {report?.rows?.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: ST.blue, marginBottom: 10 }}>Result — {report.rows.length} row{report.rows.length !== 1 ? "s" : ""}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ background: "rgba(0,53,128,0.07)" }}>{Object.keys(report.rows[0]).map(k => <th key={k} onClick={() => bulkModalSort.toggleSort(k)} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 800, fontSize: 10, textTransform: "uppercase", color: ST.blue, cursor: "pointer", userSelect: "none" }}>{k}{bulkModalSort.sortArrow(k)}</th>)}</tr></thead>
              <tbody>{bulkModalSort.sorted.map((row, i) => <tr key={i}>{Object.values(row).map((v, j) => <td key={j} style={{ padding: "7px 10px", borderBottom: `1px solid ${T.glassBdr}`, color: T.textSub }}>{String(v ?? "")}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN STUDENT MASTER 360 ──────────────────────────────────────────────────
const StudentMaster = ({ currentUserId, defaultStudentId = null, standalone = true, onBack = null, defaultCreateMode = false, onCreateDone = null }) => {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [selectedId,    setSelectedId]    = useState(null);
  const [studentInfo,   setStudentInfo]   = useState(null);
  const [activeTab,     setActiveTab]     = useState("details");
  const [createMode,    setCreateMode]    = useState(defaultCreateMode);
  const [showBulk,      setShowBulk]      = useState(false);
  const [searchDisplay, setSearchDisplay] = useState(undefined);

  const handleStudentSelect = useCallback(async (id, name, goToTab = "details") => {
    setCreateMode(false);
    if (!id) { setSelectedId(null); setStudentInfo(null); setActiveTab("details"); setSearchDisplay(""); return; }
    setStudentInfo(null);
    setSelectedId(id); setActiveTab(goToTab);
    if (name) setSearchDisplay(name);
    try {
      const r = await fetch(`${BASE}/students/${id}`);
      const d = await r.json();
      const s = d.student || null;
      setStudentInfo(s);
      if (!name && s) setSearchDisplay(`${s.first_name} ${s.last_name}`);
    } catch { setStudentInfo(null); }
  }, []);

  // When embedded in ClientMaster, pre-select the given student
  useEffect(() => {
    if (defaultStudentId && !standalone) {
      handleStudentSelect(defaultStudentId);
    }
  }, [defaultStudentId, standalone]); // eslint-disable-line

  const enterCreateMode = () => {
    setSelectedId(null); setStudentInfo(null);
    setSearchDisplay(""); setActiveTab("details");
    setCreateMode(true);
  };

  const TABS = [
    { key: "details",          label: "Details",          icon: <FaUserEdit size={13} /> },
    { key: "coaching_program", label: "Coaching Program", icon: <FaGraduationCap size={13} /> },
    { key: "login",            label: "Login",            icon: <FaChild size={13} /> },
    { key: "billing",          label: "Billing",          icon: <FaDollarSign size={13} /> },
    { key: "waiver",           label: "Waiver",           icon: <FaFileSignature size={13} /> },
    { key: "notes",            label: "Notes",            icon: <FaStickyNote size={13} /> },
    { key: "attendance",       label: "Attendance",       icon: <FaClipboardList size={13} /> },
    { key: "tournament",       label: "Tournament",       icon: <FaTrophy size={13} /> },
  ];

  const TabBar = ({ lockedExceptDetails = false }) => (
    <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
      {TABS.map(t => {
        const locked = lockedExceptDetails && t.key !== "details";
        return (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
            cursor: locked ? "default" : "pointer", WebkitTapHighlightColor: "transparent",
            display: "flex", alignItems: "center", gap: 7, letterSpacing: "0.02em",
            opacity: locked ? 0.42 : 1,
            color: activeTab === t.key ? ST.blueDark : T.textLabel,
            background: activeTab === t.key ? ST.yellowGrad : T.glass,
            backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
            border: activeTab === t.key ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
            boxShadow: activeTab === t.key ? "0 4px 14px rgba(255,200,0,0.30)" : T.glassSh,
          }}>
            {t.icon} {t.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ minHeight: "100%", padding: standalone ? "20px 28px 48px" : "0 0 48px", boxSizing: "border-box" }}>

      {/* ── Page header (standalone mode only) ── */}
      {standalone && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
          {/* Icon + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
            <div style={{ width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0, background: `linear-gradient(135deg,${ST.yellow},#fff3a0)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(255,200,0,0.32)" }}>
              <FaGraduationCap size={20} color={ST.blue} />
            </div>
            <h2 style={{ fontSize: "22px", fontWeight: 800, color: ST.blue, margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
              Student Master
            </h2>
          </div>

          {/* Search — flex-grows to fill middle space, hidden in create mode */}
          {!createMode && (
            <div style={{ flex: 1, minWidth: 220, maxWidth: 480 }}>
              <InlineSearchBar onSelect={handleStudentSelect} displayName={searchDisplay} />
            </div>
          )}

          {/* Action buttons — right side */}
          <div style={{ display: "flex", gap: 10, marginLeft: "auto", flexShrink: 0 }}>
            {createMode
              ? <GhostButton onClick={() => setCreateMode(false)}><FaTimes size={13} /> Cancel</GhostButton>
              : <PrimaryButton onClick={enterCreateMode}><FaUserPlus size={13} /> Create Student</PrimaryButton>
            }
            <GhostButton onClick={() => setShowBulk(true)}><FaUpload size={13} /> Bulk Upload</GhostButton>
          </div>
        </div>
      )}

      {/* ── Embedded mode: "Change Person" back button ── */}
      {!standalone && onBack && (
        <div style={{ marginBottom: 16 }}>
          <GhostButton onClick={onBack} style={{ display: "inline-flex", padding: "7px 14px", fontSize: 12 }}>
            <FaArrowRight size={11} style={{ transform: "rotate(180deg)" }} /> Change Person
          </GhostButton>
        </div>
      )}

      {/* ══ CREATE MODE ══ */}
      {createMode && (
        <>
          <div style={{
            background: "linear-gradient(135deg,rgba(247,206,11,0.08),rgba(3,78,123,0.06))",
            border: "1.5px dashed rgba(247,206,11,0.50)", borderRadius: 14,
            padding: "16px 20px", marginBottom: 20,
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", flexShrink: 0, background: `linear-gradient(135deg,${ST.yellow},#fff3a0)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 10px rgba(255,200,0,0.28)" }}>
              <FaUserPlus size={20} color={ST.blue} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: ST.blue, marginBottom: 3 }}>New Student</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Complete the <strong style={{ color: ST.blue }}>Details</strong> tab and save to unlock all other tabs.</div>
            </div>
          </div>

          <TabBar lockedExceptDetails={true} />

          <div style={{ ...glassCard(T), maxWidth: "900px" }}>
            {activeTab === "details"
              ? <CreateStudentForm currentUserId={currentUserId} onCreated={(newId) => handleStudentSelect(newId, undefined, "coaching_program")} />
              : <LockedTabPlaceholder tabLabel={TABS.find(t => t.key === activeTab)?.label} onGoToDetails={() => setActiveTab("details")} />
            }
          </div>
        </>
      )}

      {/* ══ STUDENT SELECTED — full 360 view ══ */}
      {!createMode && selectedId && (
        <>
          {studentInfo
            ? <StudentInfoHeader student={studentInfo} />
            : <div style={{ padding: "14px 20px", marginBottom: 20, borderRadius: 14, border: "1.5px solid rgba(3,78,123,0.12)", background: "rgba(3,78,123,0.04)", fontSize: 13, color: T.textMuted }}>Loading student…</div>
          }

          <TabBar lockedExceptDetails={false} />

          <div style={{ ...glassCard(T), maxWidth: activeTab === "billing" || activeTab === "attendance" ? "100%" : "900px" }}>
            {activeTab === "details"          && <DetailsTab studentId={selectedId} studentInfo={studentInfo} setStudentInfo={setStudentInfo} currentUserId={currentUserId} />}
            {activeTab === "coaching_program" && <CoachingProgramTab studentId={selectedId} currentUserId={currentUserId} onNext={() => setActiveTab("login")} />}
            {activeTab === "login"            && <LoginTab studentId={selectedId} studentInfo={studentInfo} currentUserId={currentUserId} onNext={() => setActiveTab("billing")} />}
            {activeTab === "billing"          && <BillingTab studentId={selectedId} currentUserId={currentUserId} />}
            {activeTab === "waiver"           && <WaiverTab studentId={selectedId} currentUserId={currentUserId} />}
            {activeTab === "notes"            && <NotesTab studentId={selectedId} currentUserId={currentUserId} />}
            {activeTab === "attendance"       && <AttendanceTab studentId={selectedId} />}
            {activeTab === "tournament"       && <TournamentStudentTab studentId={selectedId} studentName={studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : "Student"} />}
          </div>
        </>
      )}

      {/* ══ EMPTY STATE ══ */}
      {!createMode && !selectedId && (
        <div style={{ ...glassCard(T), maxWidth: 560, margin: "0 auto", textAlign: "center", padding: "48px 40px" }}>
          <div style={{ width: 60, height: 60, borderRadius: "50%", background: `linear-gradient(135deg,${ST.yellow},#fff3a0)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 4px 16px rgba(255,200,0,0.28)" }}>
            <FaSearch size={22} color={ST.blue} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: ST.blue, marginBottom: 8 }}>Select a Student</div>
          <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 24 }}>
            Search for a student by name above to open their 360° profile — details, coaching program, billing, notes, attendance and more.
          </div>
          <PrimaryButton onClick={enterCreateMode} style={{ display: "inline-flex", padding: "10px 24px" }}>
            <FaUserPlus size={13} /> Create New Student
          </PrimaryButton>
        </div>
      )}

      {showBulk && <BulkUploadModal onClose={() => setShowBulk(false)} />}
    </div>
  );
};

export default StudentMaster;
